import { __rest, __awaiter, __generator, __spreadArray, __read } from 'tslib';
import * as React from 'react';
import React__default, { forwardRef, useState, useRef, useEffect, useCallback, useMemo, useReducer, useImperativeHandle, Fragment } from 'react';
import { CircularProgress, Button as Button$1, Stack, Typography, Box, LinearProgress, Pagination as Pagination$1, Skeleton, Tab, Tabs, Paper, IconButton, List, ListItem, ListItemIcon, ListItemText, ListItemButton, Menu as Menu$1, Link as Link$2, styled, Popper, MenuList, ClickAwayListener, ToggleButton, ToggleButtonGroup, Avatar, Chip as Chip$1, InputLabel, Divider, Breadcrumbs as Breadcrumbs$1 } from '@mui/material';
import { CheckRounded, CloseRounded, ReportProblemOutlined, MoreHoriz, AddPhotoAlternate, Clear, ClearRounded, CloudDoneRounded, ErrorRounded, CloudUploadRounded, UploadRounded, DeleteRounded, DownloadRounded, VisibilityRounded, NavigateNextRounded } from '@mui/icons-material';
import { cx } from '@emotion/css';
import { useTheme, makeG2STyles } from '@komune-io/g2-themes';
import { Link as Link$1 } from 'react-router-dom';
import { TimelineItem, TimelineOppositeContent, TimelineSeparator, TimelineDot, TimelineConnector, TimelineContent, Timeline as Timeline$1 } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@komune-io/g2-notifications';
import { stringToAvatarAttributs, Base64ToFile, fileToBase64 } from '@komune-io/g2-utils';
import { MIME_TYPES, Dropzone as Dropzone$1 } from '@mantine/dropzone';
import { useLocalStorage } from '@mantine/hooks';

const ButtonBase = function (props, ref) {
    const { children, onClick, disabled = false, variant = 'contained', style, className, id, href, success = false, fail = false, warning = false, icon, noDefaultIcon, isLoading = false, component, componentProps, startIcon, color, size = 'medium' } = props, other = __rest(props, ["children", "onClick", "disabled", "variant", "style", "className", "id", "href", "success", "fail", "warning", "icon", "noDefaultIcon", "isLoading", "component", "componentProps", "startIcon", "color", "size"]);
    const forcedLoading = isLoading;
    const [loading, setloading] = useState(false);
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        if (success || fail)
            setloading(false);
    }, [success, fail]);
    const onClickMemoisied = useCallback(async (event) => {
        if (onClick) {
            setloading(true);
            await onClick(event);
            if (isMounted.current) {
                setloading(false);
            }
        }
    }, [onClick]);
    const startIconElement = useMemo(() => {
        if (loading || forcedLoading)
            return (React__default.createElement(CircularProgress, { size: size === 'small' ? 22 : size === 'medium' ? 24 : 25, sx: {
                    color: 'currentcolor',
                    margin: '-4px 0'
                } }));
        if (startIcon)
            return startIcon;
        if (icon)
            return icon;
        if (noDefaultIcon)
            return undefined;
        return success ? (React__default.createElement(CheckRounded, null)) : fail ? (React__default.createElement(CloseRounded, null)) : warning ? (React__default.createElement(ReportProblemOutlined, null)) : undefined;
    }, [
        noDefaultIcon,
        startIcon,
        icon,
        success,
        fail,
        warning,
        loading,
        forcedLoading
    ]);
    const colorSelected = useMemo(() => {
        if (warning)
            return 'warning';
        if (success)
            return 'success';
        if (fail)
            return 'error';
        return color;
    }, [color, warning, fail, success]);
    if (component)
        return (React__default.createElement(Button$1, Object.assign({ ref: ref, style: style, color: colorSelected, disabled: loading || disabled || forcedLoading, className: cx('AruiButton-root ', className), onClick: (e) => !href && onClick && onClickMemoisied(e), component: component, href: href, id: id, startIcon: startIconElement, variant: variant, size: size }, componentProps, other), children));
    return (React__default.createElement(Button$1, Object.assign({ component: href ? 'a' : 'button', 
        //@ts-ignore
        ref: ref, style: style, color: colorSelected, disabled: loading || disabled || forcedLoading, className: cx('AruiButton-root ', className), onClick: (e) => !href && onClick && onClickMemoisied(e), href: href, id: id, startIcon: startIconElement, variant: variant, size: size }, other), children));
};
const Button = forwardRef(ButtonBase);

var _path$5;
function _extends$5() { _extends$5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }
var SvgArrowLeft = function SvgArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 12 21"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    d: "M10.844 1.313 1.656 10.5l9.188 9.188"
  })));
};

const Arrow = React__default.forwardRef((props, ref) => {
    const { color = '#828282' } = props;
    return React__default.createElement(SvgArrowLeft, Object.assign({ stroke: color, ref: ref }, props));
});

var _path$4;
function _extends$4() { _extends$4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }
var SvgDelete = function SvgDelete(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 19 19"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    d: "M1.188 3.563h16.625M11.281 1.188H7.72A1.19 1.19 0 0 0 6.53 2.375v1.188h5.938V2.375a1.19 1.19 0 0 0-1.188-1.187M7.719 13.656V7.72M11.281 13.656V7.72M14.935 16.723a1.187 1.187 0 0 1-1.184 1.09H5.25a1.19 1.19 0 0 1-1.184-1.09L2.969 3.563H16.03z"
  })));
};

const Delete = React__default.forwardRef((props, ref) => {
    const { color = '#9a9a9a' } = props;
    return React__default.createElement(SvgDelete, Object.assign({ stroke: color, ref: ref }, props));
});

var _path$3;
function _extends$3() { _extends$3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }
var SvgEdit = function SvgEdit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 16 16"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    d: "M14.793 1.207a2.425 2.425 0 0 0-3.446.023l-9.666 9.667L.5 15.5l4.603-1.18 9.667-9.667a2.425 2.425 0 0 0 .023-3.446M11.071 1.507l3.423 3.422M9.675 2.903l3.422 3.422M1.68 10.897l3.427 3.42"
  })));
};

const Edit = React__default.forwardRef((props, ref) => {
    const { color = '#9a9a9a' } = props;
    return React__default.createElement(SvgEdit, Object.assign({ stroke: color, ref: ref }, props));
});

var _path$2, _path2;
function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var SvgClipboardIcon = function SvgClipboardIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 11 13"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M7.472 2.244H1.086C.486 2.244 0 2.7 0 3.26v8.56c0 .56.487 1.014 1.086 1.014h6.386c.6 0 1.086-.455 1.086-1.014v-8.56c-.003-.56-.49-1.015-1.086-1.015m.324 9.572c0 .168-.147.305-.327.305H1.083c-.18 0-.326-.137-.326-.305V3.259c0-.168.146-.305.326-.305H7.47c.18 0 .327.137.327.305z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M9.914 0H3.528C2.93 0 2.442.455 2.442 1.014c0 .198.17.355.38.355.211 0 .38-.157.38-.355 0-.168.146-.304.326-.304h6.386c.18 0 .327.136.327.304v8.56c0 .168-.146.305-.327.305-.21 0-.38.158-.38.355s.17.355.38.355c.6 0 1.086-.455 1.086-1.015v-8.56C11 .454 10.514 0 9.914 0"
  })));
};

const Clipboard = React__default.forwardRef((props, ref) => {
    const theme = useTheme();
    const { color = theme.colors.secondary } = props, other = __rest(props, ["color"]);
    return React__default.createElement(SvgClipboardIcon, Object.assign({ fill: color, ref: ref }, other));
});

var _path$1;
function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
var SvgFilledArrow = function SvgFilledArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 9 19"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M8.546 9.55.466 0v18.698z"
  })));
};

const FilledArrow = React__default.forwardRef((props, ref) => {
    const { color = '#828282' } = props;
    return React__default.createElement(SvgFilledArrow, Object.assign({ fill: color, ref: ref }, props));
});

var _path;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgRoundedArrow = function SvgRoundedArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 100 100"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "m21 79.75 62-25c4-2 4-6 0-8.5l-62-25c-3-1-7 1-7 6v46.5c0 5 4 7 7 6"
  })));
};

const RoundedArrow = React__default.forwardRef((props, ref) => {
    const { color = '#828282' } = props;
    return React__default.createElement(SvgRoundedArrow, Object.assign({ fill: color, ref: ref }, props));
});

const useStyles$a = makeG2STyles()({
    arrow: {
        width: '20px',
        height: '20px'
    }
});
const BackButtonBase = function (props, ref) {
    const { classes } = useStyles$a();
    return (React__default.createElement(Button, Object.assign({ variant: 'text', startIcon: React__default.createElement(Arrow, { className: classes.arrow }), ref: ref }, props)));
};
const BackButton = forwardRef(BackButtonBase);

const useStyles$9 = makeG2STyles()({
    icon: {
        width: '19px',
        height: '19px'
    }
});
const EditButtonBase = function (props, ref) {
    const { classes } = useStyles$9();
    return (React__default.createElement(Button, Object.assign({ variant: 'text', startIcon: React__default.createElement(Edit, { color: '#828282', className: classes.icon }), ref: ref }, props)));
};
const EditButton = forwardRef(EditButtonBase);

const useStyles$8 = makeG2STyles()({
    icon: {
        width: '20px',
        height: '20px'
    }
});
const DeleteButtonBase = function (props, ref) {
    const { classes } = useStyles$8();
    return (React__default.createElement(Button, Object.assign({ variant: 'text', startIcon: React__default.createElement(Delete, { color: '#828282', className: classes.icon }), ref: ref }, props)));
};
const DeleteButton = forwardRef(DeleteButtonBase);

const LinkButton = (props) => {
    const { to } = props, other = __rest(props, ["to"]);
    return (React__default.createElement(Button, Object.assign({ component: Link$1 }, other, { componentProps: { to: to } })));
};

const Actions = (props) => {
    const { actions = [], classes, styles, className, sx } = props, stackProps = __rest(props, ["actions", "classes", "styles", "className", "sx"]);
    const actionsDisplay = useMemo(() => {
        if (!actions || actions.length === 0)
            return undefined;
        return actions
            .filter(({ showIf = () => true }) => {
            return showIf();
        })
            .map((action) => {
            const { key, label, showIf, className, variant } = action, other = __rest(action, ["key", "label", "showIf", "className", "variant"]);
            const variantProps = variant === 'deletion'
                ? {
                    variant: 'outlined',
                    color: 'error'
                }
                : variant === 'cancellation'
                    ? {
                        variant: 'text'
                    }
                    : {
                        variant: variant
                    };
            return (React__default.createElement(Button, Object.assign({ key: key, className: cx('AruiActions-button', classes === null || classes === void 0 ? void 0 : classes.button, className), style: styles === null || styles === void 0 ? void 0 : styles.button }, variantProps, other), label));
        });
    }, [actions, classes === null || classes === void 0 ? void 0 : classes.button, styles === null || styles === void 0 ? void 0 : styles.button]);
    if (actions.length <= 0)
        return React__default.createElement(React__default.Fragment, null);
    return (React__default.createElement(Stack, Object.assign({}, stackProps, { className: cx('AruiActions-root', className), sx: Object.assign({ gap: (theme) => theme.spacing(1), flexDirection: 'row', justifyContent: 'flex-end' }, sx) }), actionsDisplay));
};

const ActionsWrapper = (props) => {
    const { actions, classes, styles, position = 'below', isLoading = false, children } = props, other = __rest(props, ["actions", "classes", "styles", "position", "isLoading", "children"]);
    const actionsElement = (React__default.createElement(Actions, Object.assign({ actions: actions !== null && actions !== void 0 ? actions : [], className: 'AruiActions-Wrapper', style: styles === null || styles === void 0 ? void 0 : styles.actions, classes: classes, styles: { button: styles === null || styles === void 0 ? void 0 : styles.button } }, other)));
    return (React__default.createElement(React__default.Fragment, null,
        (position === 'above' || position === 'both') &&
            !isLoading &&
            actionsElement,
        children,
        (position === 'below' || position === 'both') &&
            !isLoading &&
            actionsElement));
};

const useActionFeedback = () => {
    const [feedback, setFeedback] = React__default.useState(undefined);
    return {
        success: feedback !== undefined && feedback,
        fail: feedback !== undefined && !feedback,
        setFeedback
    };
};

const useStyles$7 = makeG2STyles()((theme) => ({
    dot: {
        background: theme.colors.secondary,
        position: 'relative',
        alignSelf: 'unset',
        boxShadow: 'unset'
    },
    dotPassed: {
        background: theme.colors.tertiary,
        position: 'relative',
        alignSelf: 'unset',
        boxShadow: 'unset'
    },
    separator: {
        minWidth: '50px',
        maxWidth: '50px'
    },
    item: {
        transition: '0.3s'
    },
    selectableItem: {
        cursor: 'pointer',
        '&:hover .AruiTimeLine-selectorIndicator': {
            display: 'block'
        },
        '&:hover .AruiTimeLine-item': {
            opacity: '1'
        }
    },
    itemSelected: {
        opacity: '1',
        '& .AruiTimeLine-selectorIndicator': {
            display: 'block'
        }
    },
    connector: {
        background: theme.colors.tertiary
    },
    connectorProgress: {
        background: theme.colors.primary,
        width: '100%',
        height: '100%'
    },
    ItemDisabled: {
        opacity: '0.5'
    },
    timeContainer: {
        position: 'relative',
        flex: '0.2',
        display: 'flex',
        flexDirection: 'column'
    },
    timeContainerAlternate: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
    },
    flexSeparator: {
        flex: 1
    },
    selectorIndicator: {
        display: 'none',
        width: '25px',
        height: '25px',
        strokeWidth: '1.5',
        position: 'absolute',
        left: '-15px',
        top: '2px'
    },
    timelineAlternate: {
        '& .AruiTimeLine-item-ClickableContainer:nth-of-type(even) .AruiTimeLine-item': {
            flexDirection: 'row-reverse'
        },
        '& .AruiTimeLine-item-ClickableContainer:nth-of-type(even) .AruiTimeLine-timeContainer': {
            alignItems: 'end'
        }
    },
    timelineRight: {
        '& .AruiTimeLine-item': {
            flexDirection: 'row-reverse'
        }
    },
    activeDot: {
        border: `2px solid ${theme.colors.primary}`,
        position: 'absolute',
        width: 'calc(100% + 8px)',
        height: 'calc(100% + 8px)',
        marginLeft: '-10px',
        marginTop: '-10px',
        borderRadius: '50%',
        animation: '$flashing ease 2.5s infinite'
    },
    '@keyframes flashing': {
        '0%': {
            opacity: 0
        },
        '50%': {
            opacity: 1
        },
        '80%': {
            opacity: 1
        },
        '100%': {
            opacity: 0
        }
    }
}));
/**
 * A timeline
 */
const TimelineBase = (props, ref) => {
    const { lines, classes, styles, align = 'alternate', selectedCellId, onSelectCell, passedTimeLine = false, className } = props, other = __rest(props, ["lines", "classes", "styles", "align", "selectedCellId", "onSelectCell", "passedTimeLine", "className"]);
    const theme = useTheme();
    const defaultStyles = useStyles$7();
    const linesUi = useMemo(() => lines.map((line) => {
        let isPassed = false;
        let isActive = false;
        let timeLeft = undefined;
        if (line.startDate) {
            if (line.startDate < Date.now())
                isPassed = true;
        }
        if (line.startDate && line.endDate) {
            if (line.startDate <= Date.now() && line.endDate >= Date.now()) {
                isActive = true;
                isPassed = false;
                timeLeft =
                    ((Date.now() - line.startDate) * 100) /
                        (line.endDate - line.startDate);
            }
        }
        const isSelected = !!selectedCellId && selectedCellId === line.id;
        const handleKeyDown = (event) => {
            if (event.key === 'Enter' && onSelectCell && !line.disabled) {
                onSelectCell(line);
            }
        };
        return (React__default.createElement("div", { key: line.id, onClick: () => onSelectCell && !line.disabled && onSelectCell(line), onKeyDown: handleKeyDown, className: defaultStyles.cx(onSelectCell &&
                !line.disabled &&
                defaultStyles.classes.selectableItem, 'AruiTimeLine-item-ClickableContainer') },
            React__default.createElement(TimelineItem, { key: line.id, className: defaultStyles.cx(defaultStyles.classes.item, line.disabled && defaultStyles.classes.ItemDisabled, !!selectedCellId &&
                    selectedCellId !== line.id &&
                    defaultStyles.classes.ItemDisabled, isSelected && defaultStyles.classes.itemSelected, 'AruiTimeLine-item', classes === null || classes === void 0 ? void 0 : classes.item), style: styles === null || styles === void 0 ? void 0 : styles.item },
                React__default.createElement(FilledArrow, { color: theme.colors.primary, className: defaultStyles.cx(defaultStyles.classes.selectorIndicator, 'AruiTimeLine-selectorIndicator') }),
                React__default.createElement(TimelineOppositeContent, { className: defaultStyles.cx(align === 'alternate'
                        ? defaultStyles.classes.timeContainerAlternate
                        : defaultStyles.classes.timeContainer, 'AruiTimeLine-timeContainer', classes === null || classes === void 0 ? void 0 : classes.timeContainer), style: styles === null || styles === void 0 ? void 0 : styles.timeContainer },
                    React__default.createElement(Typography, { variant: 'body2' }, line.startTime),
                    React__default.createElement("div", { className: defaultStyles.classes.flexSeparator }),
                    React__default.createElement(Typography, { variant: 'body2' }, line.endTime)),
                React__default.createElement(TimelineSeparator, { className: defaultStyles.cx(defaultStyles.classes.separator, 'AruiTimeLine-separator', classes === null || classes === void 0 ? void 0 : classes.separator), style: styles === null || styles === void 0 ? void 0 : styles.separator },
                    React__default.createElement(TimelineDot, { className: defaultStyles.cx(isPassed && !passedTimeLine
                            ? defaultStyles.classes.dotPassed
                            : defaultStyles.classes.dot, 'AruiTimeLine-startDot', classes === null || classes === void 0 ? void 0 : classes.startDot), style: styles === null || styles === void 0 ? void 0 : styles.startDot },
                        line.startDot,
                        isActive && !passedTimeLine && (React__default.createElement("div", { className: defaultStyles.classes.activeDot }))),
                    React__default.createElement(TimelineConnector, { className: defaultStyles.cx(defaultStyles.classes.connector, 'AruiTimeLine-connector', classes === null || classes === void 0 ? void 0 : classes.connector), style: styles === null || styles === void 0 ? void 0 : styles.connector },
                        timeLeft && !passedTimeLine && (React__default.createElement("div", { className: defaultStyles.classes.connectorProgress, style: { height: `${timeLeft}%` } })),
                        ((!isPassed && !timeLeft) || passedTimeLine) && (React__default.createElement("div", { className: defaultStyles.classes.connectorProgress }))),
                    line.endTime && (React__default.createElement(TimelineDot, { className: defaultStyles.cx(defaultStyles.classes.dotPassed, 'AruiTimeLine-endDot', classes === null || classes === void 0 ? void 0 : classes.endDot), style: styles === null || styles === void 0 ? void 0 : styles.endDot }, line.endDot))),
                React__default.createElement(TimelineContent, { className: defaultStyles.cx('AruiTimeLine-content', classes === null || classes === void 0 ? void 0 : classes.content), style: styles === null || styles === void 0 ? void 0 : styles.content }, line.content))));
    }), [
        lines,
        classes,
        styles,
        align,
        theme,
        selectedCellId,
        passedTimeLine,
        defaultStyles.classes
    ]);
    return (React__default.createElement(Timeline$1
    //@ts-ignore
    , Object.assign({ 
        //@ts-ignore
        ref: ref }, other, { className: defaultStyles.cx(align === 'alternate' && defaultStyles.classes.timelineAlternate, align === 'right' && defaultStyles.classes.timelineRight, 'AruiTimeLine-root', className) }), linesUi));
};
const Timeline = forwardRef(TimelineBase);

const Loading = (props) => {
    const { fullPage = true, icon, classes, styles, className } = props, other = __rest(props, ["fullPage", "icon", "classes", "styles", "className"]);
    return (React__default.createElement(Box, Object.assign({ display: 'flex', justifyContent: 'center', alignItems: 'center', width: fullPage ? '100vw' : '100%', height: fullPage ? '100vh' : '100%', className: cx('AruiLoading-iconContainer', className) }, other),
        React__default.createElement(Box, { display: 'flex', flexDirection: 'column', className: cx('AruiLoading-iconContainer', classes === null || classes === void 0 ? void 0 : classes.iconContainer), style: styles === null || styles === void 0 ? void 0 : styles.iconContainer },
            icon,
            React__default.createElement(LinearProgress, { className: cx('AruiLoading-linearProgress', classes === null || classes === void 0 ? void 0 : classes.linearProgress), style: styles === null || styles === void 0 ? void 0 : styles.linearProgress }))));
};

const Pagination = (props) => {
    const { onPageChange, page, totalPage, id, style, className, styles, classes } = props, other = __rest(props, ["onPageChange", "page", "totalPage", "id", "style", "className", "styles", "classes"]);
    const onChangePage = useCallback((_, newPage) => {
        if (page !== newPage) {
            onPageChange && onPageChange(newPage);
        }
    }, [onPageChange, page]);
    return (React__default.createElement(Box, { position: 'relative', display: 'flex', justifyContent: 'flex-end', sx: {
            marginTop: (theme) => theme.spacing(2)
        }, className: cx('AruiPagination-root', className), style: style, id: id },
        React__default.createElement(Pagination$1, Object.assign({ page: page, className: cx('AruiPagination-pagination', classes === null || classes === void 0 ? void 0 : classes.pagination), style: styles === null || styles === void 0 ? void 0 : styles.pagination, count: totalPage, onChange: onChangePage, shape: 'rounded', size: 'small', color: 'secondary', siblingCount: 0, boundaryCount: 1 }, other))));
};

const FixedPagination = (props) => {
    var _a;
    const { pagination, page, onOffsetChange, isLoading, leftElement, middleElement } = props;
    const { t } = useTranslation();
    const totalItems = useRef(undefined);
    const currentPage = useMemo(() => {
        return (pagination === null || pagination === void 0 ? void 0 : pagination.offset) / pagination.limit + 1;
    }, [pagination]);
    const total = useMemo(() => {
        if ((page === null || page === void 0 ? void 0 : page.total) && totalItems.current !== page.total)
            totalItems.current = page.total;
        return {
            page: totalItems.current
                ? Math.floor((totalItems.current - 1) / pagination.limit) + 1
                : undefined,
            items: totalItems.current
        };
    }, [page === null || page === void 0 ? void 0 : page.total, pagination.limit]);
    const handlePageChange = useCallback((pageNumber) => {
        const limit = pagination.limit;
        const offset = (pageNumber - 1) * limit;
        onOffsetChange && onOffsetChange({ offset: offset, limit: limit });
    }, [pagination.limit, onOffsetChange]);
    return (React__default.createElement(Stack, { direction: 'row', alignItems: 'center', justifyContent: 'space-between', alignContent: 'center', sx: {
            background: (theme) => theme.palette.background.default + '99',
            backdropFilter: 'blur(15px)',
            zIndex: 10,
            padding: '16px 24px',
            position: 'fixed',
            bottom: '0px',
            left: '0px',
            width: '100vw',
            borderTop: '1px solid #E0E0E0',
            '& .AruiPagination-root': {
                margin: 'unset'
            }
        } }, leftElement !== null && leftElement !== void 0 ? leftElement : React__default.createElement("div", null), middleElement !== null && middleElement !== void 0 ? middleElement : React__default.createElement("div", null),
        React__default.createElement(Stack, { direction: 'row', gap: 2, alignItems: 'center', justifyContent: 'flex-end', sx: {
                flexGrow: 1,
                flexBasis: 0
            } },
            isLoading && !total.page && (React__default.createElement(Skeleton, { sx: {
                    width: '200px',
                    height: '100%',
                    transform: 'none'
                }, animation: 'wave', variant: 'text' })),
            total.page && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Typography, { color: 'text.secondary', variant: 'caption' }, t('totalItem', {
                    start: pagination.offset + 1,
                    end: Math.min(pagination.offset + pagination.limit, (_a = total.items) !== null && _a !== void 0 ? _a : pagination.offset + pagination.limit),
                    total: total.items
                })),
                React__default.createElement(Pagination, { onPageChange: handlePageChange, page: currentPage, totalPage: total.page, siblingCount: 1 }))))));
};

const useStyles$6 = makeG2STyles()((theme) => ({
    indicator: {
        height: '100%',
        borderRadius: '20px',
        opacity: '0.15'
    },
    tabs: {
        minHeight: 0
    },
    tab: {
        minHeight: 0,
        minWidth: 0,
        borderRadius: '20px',
        padding: '5px 10px',
        textTransform: 'unset',
        color: '#808A9D',
        '&:hover': {
            color: theme.colors.primary
        }
    }
}));
const LabelSwitchBase = (props, ref) => {
    const { labels, onLabelChange, selectedLabelValue, className, style, id, classes, styles } = props, other = __rest(props, ["labels", "onLabelChange", "selectedLabelValue", "className", "style", "id", "classes", "styles"]);
    const defaultStyles = useStyles$6();
    const handleChange = useCallback((_, newValue) => {
        onLabelChange && onLabelChange(newValue);
    }, [onLabelChange]);
    const tabs = useMemo(() => labels.map((label) => (React__default.createElement(Tab, { key: label.key, className: defaultStyles.cx(defaultStyles.classes.tab, 'AruiLabelSwitch-tab', classes === null || classes === void 0 ? void 0 : classes.tab), style: styles === null || styles === void 0 ? void 0 : styles.tab, label: label.name, value: label.value }))), [labels, defaultStyles.classes.tab, classes === null || classes === void 0 ? void 0 : classes.tab, styles === null || styles === void 0 ? void 0 : styles.tab]);
    return (React__default.createElement(Tabs, Object.assign({ ref: ref, value: selectedLabelValue, classes: {
            indicator: defaultStyles.cx(defaultStyles.classes.indicator, 'AruiLabelSwitch-indicator', classes === null || classes === void 0 ? void 0 : classes.indicator)
        }, TabIndicatorProps: { style: styles === null || styles === void 0 ? void 0 : styles.indicator }, id: id, indicatorColor: 'primary', textColor: 'primary', className: defaultStyles.cx(defaultStyles.classes.tabs, 'AruiLabelSwitch-root', className), style: style, onChange: handleChange }, other), tabs));
};
const LabelSwitch = forwardRef(LabelSwitchBase);

const useStyles$5 = makeG2STyles()((theme) => ({
    root: {
        padding: `${theme.spacing * 1.5}px ${theme.spacing * 4}px`,
        display: 'inline-block',
        background: 'transparent',
        borderRadius: '0px',
        boxSizing: 'border-box',
        maxWidth: '250px'
    },
    rootLongText: {
        maxWidth: '300px',
        padding: `${theme.spacing * 1.5}px ${theme.spacing * 2}px`
    },
    composedRoot: {
        background: 'white',
        borderRadius: theme.borderRadius
    },
    content: {
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '25px',
        color: '#808A9D'
    },
    title: {
        color: '#808A9D',
        lineHeight: '19px'
    }
}));
const TicketBase = (props, ref) => {
    const { title, content, icon, variant = 'normal', className, style, id, reversed = false, classes, styles, longText = false } = props, other = __rest(props, ["title", "content", "icon", "variant", "className", "style", "id", "reversed", "classes", "styles", "longText"]);
    const defaultStyles = useStyles$5();
    return (React__default.createElement(Paper, Object.assign({ ref: ref, id: id, style: style, className: defaultStyles.cx(defaultStyles.classes.root, variant !== 'normal' && defaultStyles.classes.composedRoot, longText && defaultStyles.classes.rootLongText, 'AruiTicket-root', className), elevation: variant === 'elevated' ? 1 : 0 }, other),
        React__default.createElement(Box, { className: defaultStyles.cx('AruiTicket-baseContainer', classes === null || classes === void 0 ? void 0 : classes.baseContainer), style: styles === null || styles === void 0 ? void 0 : styles.baseContainer, display: 'flex', justifyContent: 'center', alignItems: 'center' },
            icon,
            React__default.createElement(Box, { className: defaultStyles.cx('AruiTicket-textContainer', classes === null || classes === void 0 ? void 0 : classes.textContainer), style: styles === null || styles === void 0 ? void 0 : styles.textContainer, sx: {
                    marginLeft: (theme) => icon
                        ? longText
                            ? `${theme.spacing(2)}`
                            : `${theme.spacing(2.5)}`
                        : undefined
                }, display: 'flex', flexDirection: 'column' },
                React__default.createElement(Typography, { className: defaultStyles.cx(!reversed
                        ? defaultStyles.classes.title
                        : defaultStyles.classes.content, 'AruiTicket-title', classes === null || classes === void 0 ? void 0 : classes.title), style: styles === null || styles === void 0 ? void 0 : styles.title, variant: !reversed ? 'body2' : 'subtitle1' }, title),
                React__default.createElement(Typography, { className: defaultStyles.cx(!reversed
                        ? defaultStyles.classes.content
                        : defaultStyles.classes.title, 'AruiTicket-content', classes === null || classes === void 0 ? void 0 : classes.content), style: styles === null || styles === void 0 ? void 0 : styles.content, variant: !reversed ? 'subtitle1' : 'body2' }, content)))));
};
const Ticket = forwardRef(TicketBase);

const useStyles$4 = makeG2STyles()((theme) => ({
    clipboardIcon: {
        width: '20px',
        height: '20px'
    },
    successIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.success
    }
}));
const CopyToClipboardBase = (props, ref) => {
    const { value, className, classes, styles } = props, other = __rest(props, ["value", "className", "classes", "styles"]);
    const [done, setDone] = useState(false);
    const defaultStyles = useStyles$4();
    const { t } = useTranslation();
    const onCopy = useCallback(() => {
        if (window.isSecureContext) {
            navigator.clipboard.writeText(value);
            setDone(true);
        }
        else {
            console.error('You cannot access the clipboard from a non https website');
        }
    }, [value]);
    return (React__default.createElement(Tooltip, { className: defaultStyles.cx('AruiCopyToClipboard-tooltip', classes === null || classes === void 0 ? void 0 : classes.tooltip), style: styles === null || styles === void 0 ? void 0 : styles.tooltip, helperText: done ? t('g2.copySuccess') : t('g2.copyToClipboard') },
        React__default.createElement(IconButton, Object.assign({}, other, { ref: ref, className: defaultStyles.cx('AruiCopyToClipboard-root', className), onClick: onCopy, size: 'large' }), done ? (React__default.createElement(CheckRounded, { className: defaultStyles.cx(defaultStyles.classes.successIcon, 'AruiCopyToClipboard-successIcon', classes === null || classes === void 0 ? void 0 : classes.successIcon), style: styles === null || styles === void 0 ? void 0 : styles.successIcon })) : (React__default.createElement(Clipboard, { className: defaultStyles.cx(defaultStyles.classes.clipboardIcon, 'AruiCopyToClipboard-clipBoardIcon', classes === null || classes === void 0 ? void 0 : classes.clipBoardIcon), style: styles === null || styles === void 0 ? void 0 : styles.clipBoardIcon })))));
};
const CopyToClipboard = forwardRef(CopyToClipboardBase);

/* eslint-disable react/jsx-key */
const useStyles$3 = makeG2STyles()((theme, { paddingLeft }) => ({
    item: {
        paddingLeft: `${paddingLeft}px`,
        color: 'black',
        '& .MuiListItemIcon-root': {
            color: 'black',
            minWidth: 'unset',
            marginRight: '12px'
        }
    },
    selectedItem: {
        background: `${theme.colors.primary}33`,
        color: theme.colors.primary,
        '& .MuiListItemIcon-root': {
            color: theme.colors.primary
        }
    },
    selectedTitle: {
        '& .MuiTypography-root': {
            color: theme.colors.primary
        }
    },
    itemText: {
        '& .MuiTypography-root': {
            fontSize: `${17 - paddingLeft / 10}px`,
            overflow: 'hidden',
            WebkitLineClamp: 3,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis'
        }
    },
    subList: {
        padding: '0px'
    }
}));
const Menu = (props) => {
    const { menu, classes, styles, paddingLeft, subMenuProps } = props, other = __rest(props, ["menu", "classes", "styles", "paddingLeft", "subMenuProps"]);
    const uiMenu = useMemo(() => menu.map((item) => (React__default.createElement(Item$1, Object.assign({ classes: classes, styles: styles, paddingLeft: paddingLeft, subMenuProps: subMenuProps }, item)))), [classes, styles, menu, paddingLeft, subMenuProps]);
    return React__default.createElement(List, Object.assign({}, other), uiMenu);
};
const Item$1 = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const { goto, icon, label, href, onClick, componentProps, items, component, isSelected = false, classes, styles, paddingLeft = 16, subMenuProps } = props, other = __rest(props, ["goto", "icon", "label", "href", "onClick", "componentProps", "items", "component", "isSelected", "classes", "styles", "paddingLeft", "subMenuProps"]);
    const onItemClick = useCallback(() => goto && !href && goto(), [goto, href]);
    const stylesObject = useMemo(() => ({
        paddingLeft: paddingLeft
    }), [paddingLeft]);
    const defaultStyles = useStyles$3(stylesObject);
    if (items !== undefined && items.length > 0)
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(ListItem, Object.assign({ style: (_a = styles === null || styles === void 0 ? void 0 : styles.item) === null || _a === void 0 ? void 0 : _a.root }, componentProps, other, { className: defaultStyles.cx(defaultStyles.classes.item, (_b = classes === null || classes === void 0 ? void 0 : classes.item) === null || _b === void 0 ? void 0 : _b.root) }),
                !!icon && (React__default.createElement(ListItemIcon, { className: (_c = classes === null || classes === void 0 ? void 0 : classes.item) === null || _c === void 0 ? void 0 : _c.icon, style: (_d = styles === null || styles === void 0 ? void 0 : styles.item) === null || _d === void 0 ? void 0 : _d.icon }, icon)),
                !!label && (React__default.createElement(ListItemText, { primaryTypographyProps: { color: 'inherit' }, primary: label, className: defaultStyles.cx((_e = classes === null || classes === void 0 ? void 0 : classes.item) === null || _e === void 0 ? void 0 : _e.text, defaultStyles.classes.itemText, isSelected && defaultStyles.classes.selectedTitle), style: (_f = styles === null || styles === void 0 ? void 0 : styles.item) === null || _f === void 0 ? void 0 : _f.text }))),
            React__default.createElement(Menu, Object.assign({}, subMenuProps, { className: defaultStyles.cx(defaultStyles.classes.subList, subMenuProps === null || subMenuProps === void 0 ? void 0 : subMenuProps.className), paddingLeft: paddingLeft + 10, menu: items }))));
    return (React__default.createElement(ListItemButton, Object.assign({ component: component ? component : href ? 'a' : 'div', onClick: onItemClick, href: href, style: (_g = styles === null || styles === void 0 ? void 0 : styles.item) === null || _g === void 0 ? void 0 : _g.root, selected: isSelected }, componentProps, other, { className: defaultStyles.cx(defaultStyles.classes.item, isSelected && defaultStyles.classes.selectedItem, (_h = classes === null || classes === void 0 ? void 0 : classes.item) === null || _h === void 0 ? void 0 : _h.root) }),
        !!icon && (React__default.createElement(ListItemIcon, { className: (_j = classes === null || classes === void 0 ? void 0 : classes.item) === null || _j === void 0 ? void 0 : _j.icon, style: (_k = styles === null || styles === void 0 ? void 0 : styles.item) === null || _k === void 0 ? void 0 : _k.icon }, icon)),
        !!label && (React__default.createElement(ListItemText, { primaryTypographyProps: { color: 'inherit' }, primary: label, className: defaultStyles.cx(defaultStyles.classes.itemText, (_l = classes === null || classes === void 0 ? void 0 : classes.item) === null || _l === void 0 ? void 0 : _l.text), style: (_m = styles === null || styles === void 0 ? void 0 : styles.item) === null || _m === void 0 ? void 0 : _m.text }))));
};

const useStyles$2 = makeG2STyles()((theme) => ({
    menu: {
        maxWidth: '300px',
        maxHeight: '250px',
        '& ul': {
            padding: '0'
        }
    },
    listItem: {
        padding: `${theme.spacing / 2}px ${theme.spacing * 2}px`
    }
}));
const MoreOptionsBase = (props, ref) => {
    const { options, menuContainerProps, className, classes, styles, onClick } = props, other = __rest(props, ["options", "menuContainerProps", "className", "classes", "styles", "onClick"]);
    const [anchorEl, setAnchorEl] = useState(null);
    const defaultStyles = useStyles$2();
    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        onClick && onClick(event);
    }, []);
    const close = useCallback(() => {
        setAnchorEl(null);
    }, []);
    const stopPropagation = useCallback((event) => event.stopPropagation(), []);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(IconButton, Object.assign({ ref: ref, "aria-label": 'more', "aria-controls": 'long-menu', "aria-haspopup": 'true', onClick: handleClick, className: defaultStyles.cx('AruiMoreOptions-root', className) }, other, { size: 'large' }),
            React__default.createElement(MoreHoriz, { className: defaultStyles.cx('AruiMoreOptions-moreOptionsIcon', classes === null || classes === void 0 ? void 0 : classes.moreOptionsIcon), style: styles === null || styles === void 0 ? void 0 : styles.moreOptionsIcon })),
        React__default.createElement(Menu$1, { anchorEl: anchorEl, keepMounted: true, open: Boolean(anchorEl), onClick: stopPropagation, MenuListProps: {
                onClick: stopPropagation
            }, onClose: close, PaperProps: {
                className: defaultStyles.classes.menu
            }, className: defaultStyles.cx('AruiMoreOptions-menu', classes === null || classes === void 0 ? void 0 : classes.menu), style: styles === null || styles === void 0 ? void 0 : styles.menu },
            React__default.createElement(Menu, Object.assign({ menu: options }, menuContainerProps, { classes: { item: { root: defaultStyles.classes.listItem } } })))));
};
const MoreOptions = forwardRef(MoreOptionsBase);

const useStyles$1 = makeG2STyles()({
    link: {
        opacity: '0.9',
        textDecoration: 'underline',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    disabled: {
        cursor: 'default',
        opacity: '0.5'
    }
});
const Link = (props) => {
    const { href, className, component, componentProps, disabled, variant = 'body1' } = props, other = __rest(props, ["href", "className", "component", "componentProps", "disabled", "variant"]);
    const { classes, cx } = useStyles$1();
    if (component)
        return (React__default.createElement(Link$2, Object.assign({ href: !disabled ? href : undefined, className: cx(classes.link, disabled && classes.disabled, 'AruiLink-root', className), component: component, variant: variant, color: 'inherit' }, componentProps, other)));
    return (React__default.createElement(Link$2, Object.assign({ href: !disabled ? href : undefined, className: cx(classes.link, disabled && classes.disabled, 'AruiLink-root', className), variant: variant, color: 'inherit' }, other)));
};

/* eslint-disable react/jsx-key */
const StyledPaper = styled(Paper)({
    borderRadius: '2px',
    overflow: 'hidden',
    '& .AruiContextMenu-list': {
        padding: 0
    },
    '& .MuiListItemButton-root': {
        width: '150px',
        padding: '10px',
        transition: 'none',
        color: '#353945'
    },
    '& .MuiListItemText-root': {
        margin: 0
    },
    '& .MuiListItemText-root .MuiTypography-root': {
        lineHeight: 0.8
    },
    '& .MuiListItemButton-root:hover': {
        background: '#353945',
        color: 'white'
    },
    '& .AruiContextMenu-arrow': {
        width: '15px',
        height: '15px',
        fill: 'currentColor'
    }
});
const ContextMenuBase = (props, ref) => {
    const { menu, classes, styles, onClose, anchorEl, open = false, closeOnClickAway = true, className } = props, other = __rest(props, ["menu", "classes", "styles", "onClose", "anchorEl", "open", "closeOnClickAway", "className"]);
    const [currentItem, setCurrentItem] = useState(undefined);
    const [selectedItem, setSelectedItem] = useState(undefined);
    useEffect(() => {
        setCurrentItem(undefined);
    }, [anchorEl]);
    const openPopper = !!anchorEl && open;
    const handleClickAway = useCallback(() => {
        if (closeOnClickAway && onClose && openPopper) {
            onClose();
        }
    }, [onClose, openPopper, closeOnClickAway]);
    const uiMenu = useMemo(() => menu &&
        menu.map((item) => (React__default.createElement(Item, Object.assign({ onMouseEnter: (event) => {
                if (item.items) {
                    setCurrentItem(event.currentTarget);
                    setSelectedItem(item);
                }
                else {
                    setCurrentItem(undefined);
                }
            }, isSelected: (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.key) === item.key, classes: classes, styles: styles }, item)))), [classes, styles, menu, selectedItem]);
    const subMenu = useMemo(() => {
        if (!!currentItem && !!(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.items) && openPopper) {
            return (React__default.createElement(ContextMenu, { closeOnClickAway: false, anchorEl: currentItem, menu: selectedItem.items, open: !!selectedItem && openPopper, placement: 'right-start' }));
        }
        return undefined;
    }, [currentItem, selectedItem, openPopper]);
    const popper = (React__default.createElement(Popper, Object.assign({ ref: ref, open: openPopper, anchorEl: anchorEl, className: cx('AruiContextMenu-root', className) }, other),
        React__default.createElement(StyledPaper, { className: cx('AruiContextMenu-paper', classes === null || classes === void 0 ? void 0 : classes.paper), style: styles === null || styles === void 0 ? void 0 : styles.paper },
            React__default.createElement(MenuList, { className: cx('AruiContextMenu-list', classes === null || classes === void 0 ? void 0 : classes.list), style: styles === null || styles === void 0 ? void 0 : styles.list }, uiMenu)),
        subMenu));
    if (!openPopper)
        return React__default.createElement(React__default.Fragment, null, " ");
    if (closeOnClickAway && openPopper)
        return (React__default.createElement(ClickAwayListener, { onClickAway: handleClickAway }, popper));
    return popper;
};
const Item = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { goto, icon, label, href, onClick, componentProps, items, component, isSelected = false, classes, styles } = props, other = __rest(props, ["goto", "icon", "label", "href", "onClick", "componentProps", "items", "component", "isSelected", "classes", "styles"]);
    const onItemClick = useCallback(() => goto && !href && goto(), [goto, href]);
    return (React__default.createElement(ListItemButton, Object.assign({ ref: ref, component: component ? component : href ? 'a' : 'div', onClick: onItemClick, href: href, style: (_a = styles === null || styles === void 0 ? void 0 : styles.item) === null || _a === void 0 ? void 0 : _a.root }, componentProps, other, { className: (_b = classes === null || classes === void 0 ? void 0 : classes.item) === null || _b === void 0 ? void 0 : _b.root }),
        !!icon && (React__default.createElement(ListItemIcon, { className: (_c = classes === null || classes === void 0 ? void 0 : classes.item) === null || _c === void 0 ? void 0 : _c.icon, style: (_d = styles === null || styles === void 0 ? void 0 : styles.item) === null || _d === void 0 ? void 0 : _d.icon }, icon)),
        React__default.createElement(ListItemText, { primaryTypographyProps: { color: 'inherit', variant: 'body2' }, primary: label, className: (_e = classes === null || classes === void 0 ? void 0 : classes.item) === null || _e === void 0 ? void 0 : _e.text, style: (_f = styles === null || styles === void 0 ? void 0 : styles.item) === null || _f === void 0 ? void 0 : _f.text }),
        !!items && (React__default.createElement(RoundedArrow, { className: cx('AruiContextMenu-arrow', (_g = classes === null || classes === void 0 ? void 0 : classes.item) === null || _g === void 0 ? void 0 : _g.arrow), style: (_h = styles === null || styles === void 0 ? void 0 : styles.item) === null || _h === void 0 ? void 0 : _h.arrow }))));
});
const ContextMenu = forwardRef(ContextMenuBase);

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var propTypes = {exports: {}};

var reactIs = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min;

function requireReactIs_production_min () {
	if (hasRequiredReactIs_production_min) return reactIs_production_min;
	hasRequiredReactIs_production_min = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
	reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
	reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
	reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;
	return reactIs_production_min;
}

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_development;

function requireReactIs_development () {
	if (hasRequiredReactIs_development) return reactIs_development;
	hasRequiredReactIs_development = 1;



	if (process.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development.AsyncMode = AsyncMode;
	reactIs_development.ConcurrentMode = ConcurrentMode;
	reactIs_development.ContextConsumer = ContextConsumer;
	reactIs_development.ContextProvider = ContextProvider;
	reactIs_development.Element = Element;
	reactIs_development.ForwardRef = ForwardRef;
	reactIs_development.Fragment = Fragment;
	reactIs_development.Lazy = Lazy;
	reactIs_development.Memo = Memo;
	reactIs_development.Portal = Portal;
	reactIs_development.Profiler = Profiler;
	reactIs_development.StrictMode = StrictMode;
	reactIs_development.Suspense = Suspense;
	reactIs_development.isAsyncMode = isAsyncMode;
	reactIs_development.isConcurrentMode = isConcurrentMode;
	reactIs_development.isContextConsumer = isContextConsumer;
	reactIs_development.isContextProvider = isContextProvider;
	reactIs_development.isElement = isElement;
	reactIs_development.isForwardRef = isForwardRef;
	reactIs_development.isFragment = isFragment;
	reactIs_development.isLazy = isLazy;
	reactIs_development.isMemo = isMemo;
	reactIs_development.isPortal = isPortal;
	reactIs_development.isProfiler = isProfiler;
	reactIs_development.isStrictMode = isStrictMode;
	reactIs_development.isSuspense = isSuspense;
	reactIs_development.isValidElementType = isValidElementType;
	reactIs_development.typeOf = typeOf;
	  })();
	}
	return reactIs_development;
}

var hasRequiredReactIs;

function requireReactIs () {
	if (hasRequiredReactIs) return reactIs.exports;
	hasRequiredReactIs = 1;

	if (process.env.NODE_ENV === 'production') {
	  reactIs.exports = requireReactIs_production_min();
	} else {
	  reactIs.exports = requireReactIs_development();
	}
	return reactIs.exports;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/

var objectAssign;
var hasRequiredObjectAssign;

function requireObjectAssign () {
	if (hasRequiredObjectAssign) return objectAssign;
	hasRequiredObjectAssign = 1;
	/* eslint-disable no-unused-vars */
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	function shouldUseNative() {
		try {
			if (!Object.assign) {
				return false;
			}

			// Detect buggy property enumeration order in older V8 versions.

			// https://bugs.chromium.org/p/v8/issues/detail?id=4118
			var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
			test1[5] = 'de';
			if (Object.getOwnPropertyNames(test1)[0] === '5') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test2 = {};
			for (var i = 0; i < 10; i++) {
				test2['_' + String.fromCharCode(i)] = i;
			}
			var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
				return test2[n];
			});
			if (order2.join('') !== '0123456789') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test3 = {};
			'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
				test3[letter] = letter;
			});
			if (Object.keys(Object.assign({}, test3)).join('') !==
					'abcdefghijklmnopqrst') {
				return false;
			}

			return true;
		} catch (err) {
			// We don't expect any of the above to throw, but better to be safe.
			return false;
		}
	}

	objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (getOwnPropertySymbols) {
				symbols = getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	return objectAssign;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;

function requireReactPropTypesSecret () {
	if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
	hasRequiredReactPropTypesSecret = 1;

	var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

	ReactPropTypesSecret_1 = ReactPropTypesSecret;
	return ReactPropTypesSecret_1;
}

var has;
var hasRequiredHas;

function requireHas () {
	if (hasRequiredHas) return has;
	hasRequiredHas = 1;
	has = Function.call.bind(Object.prototype.hasOwnProperty);
	return has;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var checkPropTypes_1;
var hasRequiredCheckPropTypes;

function requireCheckPropTypes () {
	if (hasRequiredCheckPropTypes) return checkPropTypes_1;
	hasRequiredCheckPropTypes = 1;

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  var ReactPropTypesSecret = requireReactPropTypesSecret();
	  var loggedTypeFailures = {};
	  var has = requireHas();

	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) { /**/ }
	  };
	}

	/**
	 * Assert that the values match with the type specs.
	 * Error messages are memorized and will only be shown once.
	 *
	 * @param {object} typeSpecs Map of name to a ReactPropType
	 * @param {object} values Runtime values that need to be type-checked
	 * @param {string} location e.g. "prop", "context", "child context"
	 * @param {string} componentName Name of the component for error messages.
	 * @param {?Function} getStack Returns the component stack.
	 * @private
	 */
	function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
	  if (process.env.NODE_ENV !== 'production') {
	    for (var typeSpecName in typeSpecs) {
	      if (has(typeSpecs, typeSpecName)) {
	        var error;
	        // Prop type validation may throw. In case they do, we don't want to
	        // fail the render phase where it didn't fail before. So we log it.
	        // After these have been cleaned up, we'll let them throw.
	        try {
	          // This is intentionally an invariant that gets caught. It's the same
	          // behavior as without this statement except with a better message.
	          if (typeof typeSpecs[typeSpecName] !== 'function') {
	            var err = Error(
	              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
	              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
	              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
	            );
	            err.name = 'Invariant Violation';
	            throw err;
	          }
	          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret);
	        } catch (ex) {
	          error = ex;
	        }
	        if (error && !(error instanceof Error)) {
	          printWarning(
	            (componentName || 'React class') + ': type specification of ' +
	            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
	            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
	            'You may have forgotten to pass an argument to the type checker ' +
	            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
	            'shape all require an argument).'
	          );
	        }
	        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
	          // Only monitor this failure once because there tends to be a lot of the
	          // same error.
	          loggedTypeFailures[error.message] = true;

	          var stack = getStack ? getStack() : '';

	          printWarning(
	            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
	          );
	        }
	      }
	    }
	  }
	}

	/**
	 * Resets warning cache when testing.
	 *
	 * @private
	 */
	checkPropTypes.resetWarningCache = function() {
	  if (process.env.NODE_ENV !== 'production') {
	    loggedTypeFailures = {};
	  }
	};

	checkPropTypes_1 = checkPropTypes;
	return checkPropTypes_1;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithTypeCheckers;
var hasRequiredFactoryWithTypeCheckers;

function requireFactoryWithTypeCheckers () {
	if (hasRequiredFactoryWithTypeCheckers) return factoryWithTypeCheckers;
	hasRequiredFactoryWithTypeCheckers = 1;

	var ReactIs = requireReactIs();
	var assign = requireObjectAssign();

	var ReactPropTypesSecret = requireReactPropTypesSecret();
	var has = requireHas();
	var checkPropTypes = requireCheckPropTypes();

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) {}
	  };
	}

	function emptyFunctionThatReturnsNull() {
	  return null;
	}

	factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
	  /* global Symbol */
	  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
	  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

	  /**
	   * Returns the iterator method function contained on the iterable object.
	   *
	   * Be sure to invoke the function with the iterable as context:
	   *
	   *     var iteratorFn = getIteratorFn(myIterable);
	   *     if (iteratorFn) {
	   *       var iterator = iteratorFn.call(myIterable);
	   *       ...
	   *     }
	   *
	   * @param {?object} maybeIterable
	   * @return {?function}
	   */
	  function getIteratorFn(maybeIterable) {
	    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
	    if (typeof iteratorFn === 'function') {
	      return iteratorFn;
	    }
	  }

	  /**
	   * Collection of methods that allow declaration and validation of props that are
	   * supplied to React components. Example usage:
	   *
	   *   var Props = require('ReactPropTypes');
	   *   var MyArticle = React.createClass({
	   *     propTypes: {
	   *       // An optional string prop named "description".
	   *       description: Props.string,
	   *
	   *       // A required enum prop named "category".
	   *       category: Props.oneOf(['News','Photos']).isRequired,
	   *
	   *       // A prop named "dialog" that requires an instance of Dialog.
	   *       dialog: Props.instanceOf(Dialog).isRequired
	   *     },
	   *     render: function() { ... }
	   *   });
	   *
	   * A more formal specification of how these methods are used:
	   *
	   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
	   *   decl := ReactPropTypes.{type}(.isRequired)?
	   *
	   * Each and every declaration produces a function with the same signature. This
	   * allows the creation of custom validation functions. For example:
	   *
	   *  var MyLink = React.createClass({
	   *    propTypes: {
	   *      // An optional string or URI prop named "href".
	   *      href: function(props, propName, componentName) {
	   *        var propValue = props[propName];
	   *        if (propValue != null && typeof propValue !== 'string' &&
	   *            !(propValue instanceof URI)) {
	   *          return new Error(
	   *            'Expected a string or an URI for ' + propName + ' in ' +
	   *            componentName
	   *          );
	   *        }
	   *      }
	   *    },
	   *    render: function() {...}
	   *  });
	   *
	   * @internal
	   */

	  var ANONYMOUS = '<<anonymous>>';

	  // Important!
	  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
	  var ReactPropTypes = {
	    array: createPrimitiveTypeChecker('array'),
	    bigint: createPrimitiveTypeChecker('bigint'),
	    bool: createPrimitiveTypeChecker('boolean'),
	    func: createPrimitiveTypeChecker('function'),
	    number: createPrimitiveTypeChecker('number'),
	    object: createPrimitiveTypeChecker('object'),
	    string: createPrimitiveTypeChecker('string'),
	    symbol: createPrimitiveTypeChecker('symbol'),

	    any: createAnyTypeChecker(),
	    arrayOf: createArrayOfTypeChecker,
	    element: createElementTypeChecker(),
	    elementType: createElementTypeTypeChecker(),
	    instanceOf: createInstanceTypeChecker,
	    node: createNodeChecker(),
	    objectOf: createObjectOfTypeChecker,
	    oneOf: createEnumTypeChecker,
	    oneOfType: createUnionTypeChecker,
	    shape: createShapeTypeChecker,
	    exact: createStrictShapeTypeChecker,
	  };

	  /**
	   * inlined Object.is polyfill to avoid requiring consumers ship their own
	   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
	   */
	  /*eslint-disable no-self-compare*/
	  function is(x, y) {
	    // SameValue algorithm
	    if (x === y) {
	      // Steps 1-5, 7-10
	      // Steps 6.b-6.e: +0 != -0
	      return x !== 0 || 1 / x === 1 / y;
	    } else {
	      // Step 6.a: NaN == NaN
	      return x !== x && y !== y;
	    }
	  }
	  /*eslint-enable no-self-compare*/

	  /**
	   * We use an Error-like object for backward compatibility as people may call
	   * PropTypes directly and inspect their output. However, we don't use real
	   * Errors anymore. We don't inspect their stack anyway, and creating them
	   * is prohibitively expensive if they are created too often, such as what
	   * happens in oneOfType() for any type before the one that matched.
	   */
	  function PropTypeError(message, data) {
	    this.message = message;
	    this.data = data && typeof data === 'object' ? data: {};
	    this.stack = '';
	  }
	  // Make `instanceof Error` still work for returned errors.
	  PropTypeError.prototype = Error.prototype;

	  function createChainableTypeChecker(validate) {
	    if (process.env.NODE_ENV !== 'production') {
	      var manualPropTypeCallCache = {};
	      var manualPropTypeWarningCount = 0;
	    }
	    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
	      componentName = componentName || ANONYMOUS;
	      propFullName = propFullName || propName;

	      if (secret !== ReactPropTypesSecret) {
	        if (throwOnDirectAccess) {
	          // New behavior only for users of `prop-types` package
	          var err = new Error(
	            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	            'Use `PropTypes.checkPropTypes()` to call them. ' +
	            'Read more at http://fb.me/use-check-prop-types'
	          );
	          err.name = 'Invariant Violation';
	          throw err;
	        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
	          // Old behavior for people using React.PropTypes
	          var cacheKey = componentName + ':' + propName;
	          if (
	            !manualPropTypeCallCache[cacheKey] &&
	            // Avoid spamming the console because they are often not actionable except for lib authors
	            manualPropTypeWarningCount < 3
	          ) {
	            printWarning(
	              'You are manually calling a React.PropTypes validation ' +
	              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
	              'and will throw in the standalone `prop-types` package. ' +
	              'You may be seeing this warning due to a third-party PropTypes ' +
	              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
	            );
	            manualPropTypeCallCache[cacheKey] = true;
	            manualPropTypeWarningCount++;
	          }
	        }
	      }
	      if (props[propName] == null) {
	        if (isRequired) {
	          if (props[propName] === null) {
	            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
	          }
	          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
	        }
	        return null;
	      } else {
	        return validate(props, propName, componentName, location, propFullName);
	      }
	    }

	    var chainedCheckType = checkType.bind(null, false);
	    chainedCheckType.isRequired = checkType.bind(null, true);

	    return chainedCheckType;
	  }

	  function createPrimitiveTypeChecker(expectedType) {
	    function validate(props, propName, componentName, location, propFullName, secret) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== expectedType) {
	        // `propValue` being instance of, say, date/regexp, pass the 'object'
	        // check, but we can offer a more precise error message here rather than
	        // 'of type `object`'.
	        var preciseType = getPreciseType(propValue);

	        return new PropTypeError(
	          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
	          {expectedType: expectedType}
	        );
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createAnyTypeChecker() {
	    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
	  }

	  function createArrayOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
	      }
	      var propValue = props[propName];
	      if (!Array.isArray(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
	      }
	      for (var i = 0; i < propValue.length; i++) {
	        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret);
	        if (error instanceof Error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!isValidElement(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!ReactIs.isValidElementType(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createInstanceTypeChecker(expectedClass) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!(props[propName] instanceof expectedClass)) {
	        var expectedClassName = expectedClass.name || ANONYMOUS;
	        var actualClassName = getClassName(props[propName]);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createEnumTypeChecker(expectedValues) {
	    if (!Array.isArray(expectedValues)) {
	      if (process.env.NODE_ENV !== 'production') {
	        if (arguments.length > 1) {
	          printWarning(
	            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
	            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
	          );
	        } else {
	          printWarning('Invalid argument supplied to oneOf, expected an array.');
	        }
	      }
	      return emptyFunctionThatReturnsNull;
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      for (var i = 0; i < expectedValues.length; i++) {
	        if (is(propValue, expectedValues[i])) {
	          return null;
	        }
	      }

	      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
	        var type = getPreciseType(value);
	        if (type === 'symbol') {
	          return String(value);
	        }
	        return value;
	      });
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createObjectOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
	      }
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
	      }
	      for (var key in propValue) {
	        if (has(propValue, key)) {
	          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	          if (error instanceof Error) {
	            return error;
	          }
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createUnionTypeChecker(arrayOfTypeCheckers) {
	    if (!Array.isArray(arrayOfTypeCheckers)) {
	      process.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
	      return emptyFunctionThatReturnsNull;
	    }

	    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	      var checker = arrayOfTypeCheckers[i];
	      if (typeof checker !== 'function') {
	        printWarning(
	          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
	          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
	        );
	        return emptyFunctionThatReturnsNull;
	      }
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var expectedTypes = [];
	      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	        var checker = arrayOfTypeCheckers[i];
	        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret);
	        if (checkerResult == null) {
	          return null;
	        }
	        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
	          expectedTypes.push(checkerResult.data.expectedType);
	        }
	      }
	      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createNodeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!isNode(props[propName])) {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function invalidValidatorError(componentName, location, propFullName, key, type) {
	    return new PropTypeError(
	      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
	      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
	    );
	  }

	  function createShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      for (var key in shapeTypes) {
	        var checker = shapeTypes[key];
	        if (typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createStrictShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      // We need to check all keys in case some are required but missing from props.
	      var allKeys = assign({}, props[propName], shapeTypes);
	      for (var key in allKeys) {
	        var checker = shapeTypes[key];
	        if (has(shapeTypes, key) && typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        if (!checker) {
	          return new PropTypeError(
	            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
	            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
	            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
	          );
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }

	    return createChainableTypeChecker(validate);
	  }

	  function isNode(propValue) {
	    switch (typeof propValue) {
	      case 'number':
	      case 'string':
	      case 'undefined':
	        return true;
	      case 'boolean':
	        return !propValue;
	      case 'object':
	        if (Array.isArray(propValue)) {
	          return propValue.every(isNode);
	        }
	        if (propValue === null || isValidElement(propValue)) {
	          return true;
	        }

	        var iteratorFn = getIteratorFn(propValue);
	        if (iteratorFn) {
	          var iterator = iteratorFn.call(propValue);
	          var step;
	          if (iteratorFn !== propValue.entries) {
	            while (!(step = iterator.next()).done) {
	              if (!isNode(step.value)) {
	                return false;
	              }
	            }
	          } else {
	            // Iterator will provide entry [k,v] tuples rather than values.
	            while (!(step = iterator.next()).done) {
	              var entry = step.value;
	              if (entry) {
	                if (!isNode(entry[1])) {
	                  return false;
	                }
	              }
	            }
	          }
	        } else {
	          return false;
	        }

	        return true;
	      default:
	        return false;
	    }
	  }

	  function isSymbol(propType, propValue) {
	    // Native Symbol.
	    if (propType === 'symbol') {
	      return true;
	    }

	    // falsy value can't be a Symbol
	    if (!propValue) {
	      return false;
	    }

	    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
	    if (propValue['@@toStringTag'] === 'Symbol') {
	      return true;
	    }

	    // Fallback for non-spec compliant Symbols which are polyfilled.
	    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
	      return true;
	    }

	    return false;
	  }

	  // Equivalent of `typeof` but with special handling for array and regexp.
	  function getPropType(propValue) {
	    var propType = typeof propValue;
	    if (Array.isArray(propValue)) {
	      return 'array';
	    }
	    if (propValue instanceof RegExp) {
	      // Old webkits (at least until Android 4.0) return 'function' rather than
	      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
	      // passes PropTypes.object.
	      return 'object';
	    }
	    if (isSymbol(propType, propValue)) {
	      return 'symbol';
	    }
	    return propType;
	  }

	  // This handles more types than `getPropType`. Only used for error messages.
	  // See `createPrimitiveTypeChecker`.
	  function getPreciseType(propValue) {
	    if (typeof propValue === 'undefined' || propValue === null) {
	      return '' + propValue;
	    }
	    var propType = getPropType(propValue);
	    if (propType === 'object') {
	      if (propValue instanceof Date) {
	        return 'date';
	      } else if (propValue instanceof RegExp) {
	        return 'regexp';
	      }
	    }
	    return propType;
	  }

	  // Returns a string that is postfixed to a warning about an invalid type.
	  // For example, "undefined" or "of type array"
	  function getPostfixForTypeWarning(value) {
	    var type = getPreciseType(value);
	    switch (type) {
	      case 'array':
	      case 'object':
	        return 'an ' + type;
	      case 'boolean':
	      case 'date':
	      case 'regexp':
	        return 'a ' + type;
	      default:
	        return type;
	    }
	  }

	  // Returns class name of the object, if any.
	  function getClassName(propValue) {
	    if (!propValue.constructor || !propValue.constructor.name) {
	      return ANONYMOUS;
	    }
	    return propValue.constructor.name;
	  }

	  ReactPropTypes.checkPropTypes = checkPropTypes;
	  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithTypeCheckers;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;

function requireFactoryWithThrowingShims () {
	if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
	hasRequiredFactoryWithThrowingShims = 1;

	var ReactPropTypesSecret = requireReactPropTypesSecret();

	function emptyFunction() {}
	function emptyFunctionWithReset() {}
	emptyFunctionWithReset.resetWarningCache = emptyFunction;

	factoryWithThrowingShims = function() {
	  function shim(props, propName, componentName, location, propFullName, secret) {
	    if (secret === ReactPropTypesSecret) {
	      // It is still safe when called from React.
	      return;
	    }
	    var err = new Error(
	      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	      'Use PropTypes.checkPropTypes() to call them. ' +
	      'Read more at http://fb.me/use-check-prop-types'
	    );
	    err.name = 'Invariant Violation';
	    throw err;
	  }	  shim.isRequired = shim;
	  function getShim() {
	    return shim;
	  }	  // Important!
	  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
	  var ReactPropTypes = {
	    array: shim,
	    bigint: shim,
	    bool: shim,
	    func: shim,
	    number: shim,
	    object: shim,
	    string: shim,
	    symbol: shim,

	    any: shim,
	    arrayOf: getShim,
	    element: shim,
	    elementType: shim,
	    instanceOf: getShim,
	    node: shim,
	    objectOf: getShim,
	    oneOf: getShim,
	    oneOfType: getShim,
	    shape: getShim,
	    exact: getShim,

	    checkPropTypes: emptyFunctionWithReset,
	    resetWarningCache: emptyFunction
	  };

	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithThrowingShims;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = requireReactIs();

  // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod
  var throwOnDirectAccess = true;
  propTypes.exports = requireFactoryWithTypeCheckers()(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  propTypes.exports = requireFactoryWithThrowingShims()();
}

var propTypesExports = propTypes.exports;
var PropTypes = /*@__PURE__*/getDefaultExportFromCjs(propTypesExports);

var COMMON_MIME_TYPES = new Map([
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
    ['aac', 'audio/aac'],
    ['abw', 'application/x-abiword'],
    ['arc', 'application/x-freearc'],
    ['avif', 'image/avif'],
    ['avi', 'video/x-msvideo'],
    ['azw', 'application/vnd.amazon.ebook'],
    ['bin', 'application/octet-stream'],
    ['bmp', 'image/bmp'],
    ['bz', 'application/x-bzip'],
    ['bz2', 'application/x-bzip2'],
    ['cda', 'application/x-cdf'],
    ['csh', 'application/x-csh'],
    ['css', 'text/css'],
    ['csv', 'text/csv'],
    ['doc', 'application/msword'],
    ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    ['eot', 'application/vnd.ms-fontobject'],
    ['epub', 'application/epub+zip'],
    ['gz', 'application/gzip'],
    ['gif', 'image/gif'],
    ['heic', 'image/heic'],
    ['heif', 'image/heif'],
    ['htm', 'text/html'],
    ['html', 'text/html'],
    ['ico', 'image/vnd.microsoft.icon'],
    ['ics', 'text/calendar'],
    ['jar', 'application/java-archive'],
    ['jpeg', 'image/jpeg'],
    ['jpg', 'image/jpeg'],
    ['js', 'text/javascript'],
    ['json', 'application/json'],
    ['jsonld', 'application/ld+json'],
    ['mid', 'audio/midi'],
    ['midi', 'audio/midi'],
    ['mjs', 'text/javascript'],
    ['mp3', 'audio/mpeg'],
    ['mp4', 'video/mp4'],
    ['mpeg', 'video/mpeg'],
    ['mpkg', 'application/vnd.apple.installer+xml'],
    ['odp', 'application/vnd.oasis.opendocument.presentation'],
    ['ods', 'application/vnd.oasis.opendocument.spreadsheet'],
    ['odt', 'application/vnd.oasis.opendocument.text'],
    ['oga', 'audio/ogg'],
    ['ogv', 'video/ogg'],
    ['ogx', 'application/ogg'],
    ['opus', 'audio/opus'],
    ['otf', 'font/otf'],
    ['png', 'image/png'],
    ['pdf', 'application/pdf'],
    ['php', 'application/x-httpd-php'],
    ['ppt', 'application/vnd.ms-powerpoint'],
    ['pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    ['rar', 'application/vnd.rar'],
    ['rtf', 'application/rtf'],
    ['sh', 'application/x-sh'],
    ['svg', 'image/svg+xml'],
    ['swf', 'application/x-shockwave-flash'],
    ['tar', 'application/x-tar'],
    ['tif', 'image/tiff'],
    ['tiff', 'image/tiff'],
    ['ts', 'video/mp2t'],
    ['ttf', 'font/ttf'],
    ['txt', 'text/plain'],
    ['vsd', 'application/vnd.visio'],
    ['wav', 'audio/wav'],
    ['weba', 'audio/webm'],
    ['webm', 'video/webm'],
    ['webp', 'image/webp'],
    ['woff', 'font/woff'],
    ['woff2', 'font/woff2'],
    ['xhtml', 'application/xhtml+xml'],
    ['xls', 'application/vnd.ms-excel'],
    ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    ['xml', 'application/xml'],
    ['xul', 'application/vnd.mozilla.xul+xml'],
    ['zip', 'application/zip'],
    ['7z', 'application/x-7z-compressed'],
    // Others
    ['mkv', 'video/x-matroska'],
    ['mov', 'video/quicktime'],
    ['msg', 'application/vnd.ms-outlook']
]);
function toFileWithPath(file, path) {
    var f = withMimeType(file);
    if (typeof f.path !== 'string') { // on electron, path is already set to the absolute path
        var webkitRelativePath = file.webkitRelativePath;
        Object.defineProperty(f, 'path', {
            value: typeof path === 'string'
                ? path
                // If <input webkitdirectory> is set,
                // the File will have a {webkitRelativePath} property
                // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory
                : typeof webkitRelativePath === 'string' && webkitRelativePath.length > 0
                    ? webkitRelativePath
                    : file.name,
            writable: false,
            configurable: false,
            enumerable: true
        });
    }
    return f;
}
function withMimeType(file) {
    var name = file.name;
    var hasExtension = name && name.lastIndexOf('.') !== -1;
    if (hasExtension && !file.type) {
        var ext = name.split('.')
            .pop().toLowerCase();
        var type = COMMON_MIME_TYPES.get(ext);
        if (type) {
            Object.defineProperty(file, 'type', {
                value: type,
                writable: false,
                configurable: false,
                enumerable: true
            });
        }
    }
    return file;
}

var FILES_TO_IGNORE = [
    // Thumbnail cache files for macOS and Windows
    '.DS_Store',
    'Thumbs.db' // Windows
];
/**
 * Convert a DragEvent's DataTrasfer object to a list of File objects
 * NOTE: If some of the items are folders,
 * everything will be flattened and placed in the same list but the paths will be kept as a {path} property.
 *
 * EXPERIMENTAL: A list of https://developer.mozilla.org/en-US/docs/Web/API/FileSystemHandle objects can also be passed as an arg
 * and a list of File objects will be returned.
 *
 * @param evt
 */
function fromEvent(evt) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (isObject(evt) && isDataTransfer(evt.dataTransfer)) {
                return [2 /*return*/, getDataTransferFiles(evt.dataTransfer, evt.type)];
            }
            else if (isChangeEvt(evt)) {
                return [2 /*return*/, getInputFiles(evt)];
            }
            else if (Array.isArray(evt) && evt.every(function (item) { return 'getFile' in item && typeof item.getFile === 'function'; })) {
                return [2 /*return*/, getFsHandleFiles(evt)];
            }
            return [2 /*return*/, []];
        });
    });
}
function isDataTransfer(value) {
    return isObject(value);
}
function isChangeEvt(value) {
    return isObject(value) && isObject(value.target);
}
function isObject(v) {
    return typeof v === 'object' && v !== null;
}
function getInputFiles(evt) {
    return fromList(evt.target.files).map(function (file) { return toFileWithPath(file); });
}
// Ee expect each handle to be https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle
function getFsHandleFiles(handles) {
    return __awaiter(this, void 0, void 0, function () {
        var files;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Promise.all(handles.map(function (h) { return h.getFile(); }))];
                case 1:
                    files = _a.sent();
                    return [2 /*return*/, files.map(function (file) { return toFileWithPath(file); })];
            }
        });
    });
}
function getDataTransferFiles(dt, type) {
    return __awaiter(this, void 0, void 0, function () {
        var items, files;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!dt.items) return [3 /*break*/, 2];
                    items = fromList(dt.items)
                        .filter(function (item) { return item.kind === 'file'; });
                    // According to https://html.spec.whatwg.org/multipage/dnd.html#dndevents,
                    // only 'dragstart' and 'drop' has access to the data (source node)
                    if (type !== 'drop') {
                        return [2 /*return*/, items];
                    }
                    return [4 /*yield*/, Promise.all(items.map(toFilePromises))];
                case 1:
                    files = _a.sent();
                    return [2 /*return*/, noIgnoredFiles(flatten(files))];
                case 2: return [2 /*return*/, noIgnoredFiles(fromList(dt.files)
                        .map(function (file) { return toFileWithPath(file); }))];
            }
        });
    });
}
function noIgnoredFiles(files) {
    return files.filter(function (file) { return FILES_TO_IGNORE.indexOf(file.name) === -1; });
}
// IE11 does not support Array.from()
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from#Browser_compatibility
// https://developer.mozilla.org/en-US/docs/Web/API/FileList
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransferItemList
function fromList(items) {
    if (items === null) {
        return [];
    }
    var files = [];
    // tslint:disable: prefer-for-of
    for (var i = 0; i < items.length; i++) {
        var file = items[i];
        files.push(file);
    }
    return files;
}
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransferItem
function toFilePromises(item) {
    if (typeof item.webkitGetAsEntry !== 'function') {
        return fromDataTransferItem(item);
    }
    var entry = item.webkitGetAsEntry();
    // Safari supports dropping an image node from a different window and can be retrieved using
    // the DataTransferItem.getAsFile() API
    // NOTE: FileSystemEntry.file() throws if trying to get the file
    if (entry && entry.isDirectory) {
        return fromDirEntry(entry);
    }
    return fromDataTransferItem(item);
}
function flatten(items) {
    return items.reduce(function (acc, files) { return __spreadArray(__spreadArray([], __read(acc), false), __read((Array.isArray(files) ? flatten(files) : [files])), false); }, []);
}
function fromDataTransferItem(item) {
    var file = item.getAsFile();
    if (!file) {
        return Promise.reject("".concat(item, " is not a File"));
    }
    var fwp = toFileWithPath(file);
    return Promise.resolve(fwp);
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemEntry
function fromEntry(entry) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, entry.isDirectory ? fromDirEntry(entry) : fromFileEntry(entry)];
        });
    });
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry
function fromDirEntry(entry) {
    var reader = entry.createReader();
    return new Promise(function (resolve, reject) {
        var entries = [];
        function readEntries() {
            var _this = this;
            // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry/createReader
            // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryReader/readEntries
            reader.readEntries(function (batch) { return __awaiter(_this, void 0, void 0, function () {
                var files, err_1, items;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!!batch.length) return [3 /*break*/, 5];
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, Promise.all(entries)];
                        case 2:
                            files = _a.sent();
                            resolve(files);
                            return [3 /*break*/, 4];
                        case 3:
                            err_1 = _a.sent();
                            reject(err_1);
                            return [3 /*break*/, 4];
                        case 4: return [3 /*break*/, 6];
                        case 5:
                            items = Promise.all(batch.map(fromEntry));
                            entries.push(items);
                            // Continue reading
                            readEntries();
                            _a.label = 6;
                        case 6: return [2 /*return*/];
                    }
                });
            }); }, function (err) {
                reject(err);
            });
        }
        readEntries();
    });
}
// https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileEntry
function fromFileEntry(entry) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve, reject) {
                    entry.file(function (file) {
                        var fwp = toFileWithPath(file, entry.fullPath);
                        resolve(fwp);
                    }, function (err) {
                        reject(err);
                    });
                })];
        });
    });
}

var _default = function (file, acceptedFiles) {
  if (file && acceptedFiles) {
    var acceptedFilesArray = Array.isArray(acceptedFiles) ? acceptedFiles : acceptedFiles.split(',');
    var fileName = file.name || '';
    var mimeType = (file.type || '').toLowerCase();
    var baseMimeType = mimeType.replace(/\/.*$/, '');
    return acceptedFilesArray.some(function (type) {
      var validType = type.trim().toLowerCase();

      if (validType.charAt(0) === '.') {
        return fileName.toLowerCase().endsWith(validType);
      } else if (validType.endsWith('/*')) {
        // This is something like a image/* mime type
        return baseMimeType === validType.replace(/\/.*$/, '');
      }

      return mimeType === validType;
    });
  }

  return true;
};

function _toConsumableArray$1(arr) { return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1(); }

function _nonIterableSpread$1() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray$1(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$1(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty$1(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$1(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

var FILE_INVALID_TYPE = "file-invalid-type";
var FILE_TOO_LARGE = "file-too-large";
var FILE_TOO_SMALL = "file-too-small";
var TOO_MANY_FILES = "too-many-files";

var getInvalidTypeRejectionErr = function getInvalidTypeRejectionErr(accept) {
  accept = Array.isArray(accept) && accept.length === 1 ? accept[0] : accept;
  var messageSuffix = Array.isArray(accept) ? "one of ".concat(accept.join(", ")) : accept;
  return {
    code: FILE_INVALID_TYPE,
    message: "File type must be ".concat(messageSuffix)
  };
};
var getTooLargeRejectionErr = function getTooLargeRejectionErr(maxSize) {
  return {
    code: FILE_TOO_LARGE,
    message: "File is larger than ".concat(maxSize, " ").concat(maxSize === 1 ? "byte" : "bytes")
  };
};
var getTooSmallRejectionErr = function getTooSmallRejectionErr(minSize) {
  return {
    code: FILE_TOO_SMALL,
    message: "File is smaller than ".concat(minSize, " ").concat(minSize === 1 ? "byte" : "bytes")
  };
};
var TOO_MANY_FILES_REJECTION = {
  code: TOO_MANY_FILES,
  message: "Too many files"
}; // Firefox versions prior to 53 return a bogus MIME type for every file drag, so dragovers with
// that MIME type will always be accepted

function fileAccepted(file, accept) {
  var isAcceptable = file.type === "application/x-moz-file" || _default(file, accept);
  return [isAcceptable, isAcceptable ? null : getInvalidTypeRejectionErr(accept)];
}
function fileMatchSize(file, minSize, maxSize) {
  if (isDefined(file.size)) {
    if (isDefined(minSize) && isDefined(maxSize)) {
      if (file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
      if (file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
    } else if (isDefined(minSize) && file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];else if (isDefined(maxSize) && file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
  }

  return [true, null];
}

function isDefined(value) {
  return value !== undefined && value !== null;
}
/**
 *
 * @param {object} options
 * @param {File[]} options.files
 * @param {string|string[]} [options.accept]
 * @param {number} [options.minSize]
 * @param {number} [options.maxSize]
 * @param {boolean} [options.multiple]
 * @param {number} [options.maxFiles]
 * @param {(f: File) => FileError|FileError[]|null} [options.validator]
 * @returns
 */


function allFilesAccepted(_ref) {
  var files = _ref.files,
      accept = _ref.accept,
      minSize = _ref.minSize,
      maxSize = _ref.maxSize,
      multiple = _ref.multiple,
      maxFiles = _ref.maxFiles,
      validator = _ref.validator;

  if (!multiple && files.length > 1 || multiple && maxFiles >= 1 && files.length > maxFiles) {
    return false;
  }

  return files.every(function (file) {
    var _fileAccepted = fileAccepted(file, accept),
        _fileAccepted2 = _slicedToArray$1(_fileAccepted, 1),
        accepted = _fileAccepted2[0];

    var _fileMatchSize = fileMatchSize(file, minSize, maxSize),
        _fileMatchSize2 = _slicedToArray$1(_fileMatchSize, 1),
        sizeMatch = _fileMatchSize2[0];

    var customErrors = validator ? validator(file) : null;
    return accepted && sizeMatch && !customErrors;
  });
} // React's synthetic events has event.isPropagationStopped,
// but to remain compatibility with other libs (Preact) fall back
// to check event.cancelBubble

function isPropagationStopped(event) {
  if (typeof event.isPropagationStopped === "function") {
    return event.isPropagationStopped();
  } else if (typeof event.cancelBubble !== "undefined") {
    return event.cancelBubble;
  }

  return false;
}
function isEvtWithFiles(event) {
  if (!event.dataTransfer) {
    return !!event.target && !!event.target.files;
  } // https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/types
  // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Recommended_drag_types#file


  return Array.prototype.some.call(event.dataTransfer.types, function (type) {
    return type === "Files" || type === "application/x-moz-file";
  });
}

function onDocumentDragOver(event) {
  event.preventDefault();
}

function isIe(userAgent) {
  return userAgent.indexOf("MSIE") !== -1 || userAgent.indexOf("Trident/") !== -1;
}

function isEdge(userAgent) {
  return userAgent.indexOf("Edge/") !== -1;
}

function isIeOrEdge() {
  var userAgent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.navigator.userAgent;
  return isIe(userAgent) || isEdge(userAgent);
}
/**
 * This is intended to be used to compose event handlers
 * They are executed in order until one of them calls `event.isPropagationStopped()`.
 * Note that the check is done on the first invoke too,
 * meaning that if propagation was stopped before invoking the fns,
 * no handlers will be executed.
 *
 * @param {Function} fns the event hanlder functions
 * @return {Function} the event handler to add to an element
 */

function composeEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    return fns.some(function (fn) {
      if (!isPropagationStopped(event) && fn) {
        fn.apply(void 0, [event].concat(args));
      }

      return isPropagationStopped(event);
    });
  };
}
/**
 * canUseFileSystemAccessAPI checks if the [File System Access API](https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API)
 * is supported by the browser.
 * @returns {boolean}
 */

function canUseFileSystemAccessAPI() {
  return "showOpenFilePicker" in window;
}
/**
 * Convert the `{accept}` dropzone prop to the
 * `{types}` option for https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker
 *
 * @param {AcceptProp} accept
 * @returns {{accept: string[]}[]}
 */

function pickerOptionsFromAccept(accept) {
  if (isDefined(accept)) {
    var acceptForPicker = Object.entries(accept).filter(function (_ref2) {
      var _ref3 = _slicedToArray$1(_ref2, 2),
          mimeType = _ref3[0],
          ext = _ref3[1];

      var ok = true;

      if (!isMIMEType(mimeType)) {
        console.warn("Skipped \"".concat(mimeType, "\" because it is not a valid MIME type. Check https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types for a list of valid MIME types."));
        ok = false;
      }

      if (!Array.isArray(ext) || !ext.every(isExt)) {
        console.warn("Skipped \"".concat(mimeType, "\" because an invalid file extension was provided."));
        ok = false;
      }

      return ok;
    }).reduce(function (agg, _ref4) {
      var _ref5 = _slicedToArray$1(_ref4, 2),
          mimeType = _ref5[0],
          ext = _ref5[1];

      return _objectSpread$1(_objectSpread$1({}, agg), {}, _defineProperty$1({}, mimeType, ext));
    }, {});
    return [{
      // description is required due to https://crbug.com/1264708
      description: "Files",
      accept: acceptForPicker
    }];
  }

  return accept;
}
/**
 * Convert the `{accept}` dropzone prop to an array of MIME types/extensions.
 * @param {AcceptProp} accept
 * @returns {string}
 */

function acceptPropAsAcceptAttr(accept) {
  if (isDefined(accept)) {
    return Object.entries(accept).reduce(function (a, _ref6) {
      var _ref7 = _slicedToArray$1(_ref6, 2),
          mimeType = _ref7[0],
          ext = _ref7[1];

      return [].concat(_toConsumableArray$1(a), [mimeType], _toConsumableArray$1(ext));
    }, []) // Silently discard invalid entries as pickerOptionsFromAccept warns about these
    .filter(function (v) {
      return isMIMEType(v) || isExt(v);
    }).join(",");
  }

  return undefined;
}
/**
 * Check if v is an exception caused by aborting a request (e.g window.showOpenFilePicker()).
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/DOMException.
 * @param {any} v
 * @returns {boolean} True if v is an abort exception.
 */

function isAbort(v) {
  return v instanceof DOMException && (v.name === "AbortError" || v.code === v.ABORT_ERR);
}
/**
 * Check if v is a security error.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/DOMException.
 * @param {any} v
 * @returns {boolean} True if v is a security error.
 */

function isSecurityError(v) {
  return v instanceof DOMException && (v.name === "SecurityError" || v.code === v.SECURITY_ERR);
}
/**
 * Check if v is a MIME type string.
 *
 * See accepted format: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers.
 *
 * @param {string} v
 */

function isMIMEType(v) {
  return v === "audio/*" || v === "video/*" || v === "image/*" || v === "text/*" || /\w+\/[-+.\w]+/g.test(v);
}
/**
 * Check if v is a file extension.
 * @param {string} v
 */

function isExt(v) {
  return /^.*\.[\w]+$/.test(v);
}
/**
 * @typedef {Object.<string, string[]>} AcceptProp
 */

/**
 * @typedef {object} FileError
 * @property {string} message
 * @property {ErrorCode|string} code
 */

/**
 * @typedef {"file-invalid-type"|"file-too-large"|"file-too-small"|"too-many-files"} ErrorCode
 */

var _excluded = ["children"],
    _excluded2 = ["open"],
    _excluded3 = ["refKey", "role", "onKeyDown", "onFocus", "onBlur", "onClick", "onDragEnter", "onDragOver", "onDragLeave", "onDrop"],
    _excluded4 = ["refKey", "onChange", "onClick"];

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * Convenience wrapper component for the `useDropzone` hook
 *
 * ```jsx
 * <Dropzone>
 *   {({getRootProps, getInputProps}) => (
 *     <div {...getRootProps()}>
 *       <input {...getInputProps()} />
 *       <p>Drag 'n' drop some files here, or click to select files</p>
 *     </div>
 *   )}
 * </Dropzone>
 * ```
 */

var Dropzone = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      params = _objectWithoutProperties(_ref, _excluded);

  var _useDropzone = useDropzone(params),
      open = _useDropzone.open,
      props = _objectWithoutProperties(_useDropzone, _excluded2);

  useImperativeHandle(ref, function () {
    return {
      open: open
    };
  }, [open]); // TODO: Figure out why react-styleguidist cannot create docs if we don't return a jsx element

  return /*#__PURE__*/React__default.createElement(Fragment, null, children(_objectSpread(_objectSpread({}, props), {}, {
    open: open
  })));
});
Dropzone.displayName = "Dropzone"; // Add default props for react-docgen

var defaultProps = {
  disabled: false,
  getFilesFromEvent: fromEvent,
  maxSize: Infinity,
  minSize: 0,
  multiple: true,
  maxFiles: 0,
  preventDropOnDocument: true,
  noClick: false,
  noKeyboard: false,
  noDrag: false,
  noDragEventsBubbling: false,
  validator: null,
  useFsAccessApi: true,
  autoFocus: false
};
Dropzone.defaultProps = defaultProps;
Dropzone.propTypes = {
  /**
   * Render function that exposes the dropzone state and prop getter fns
   *
   * @param {object} params
   * @param {Function} params.getRootProps Returns the props you should apply to the root drop container you render
   * @param {Function} params.getInputProps Returns the props you should apply to hidden file input you render
   * @param {Function} params.open Open the native file selection dialog
   * @param {boolean} params.isFocused Dropzone area is in focus
   * @param {boolean} params.isFileDialogActive File dialog is opened
   * @param {boolean} params.isDragActive Active drag is in progress
   * @param {boolean} params.isDragAccept Dragged files are accepted
   * @param {boolean} params.isDragReject Some dragged files are rejected
   * @param {File[]} params.acceptedFiles Accepted files
   * @param {FileRejection[]} params.fileRejections Rejected files and why they were rejected
   */
  children: PropTypes.func,

  /**
   * Set accepted file types.
   * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information.
   * Keep in mind that mime type determination is not reliable across platforms. CSV files,
   * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
   * Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
   */
  accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),

  /**
   * Allow drag 'n' drop (or selection from the file dialog) of multiple files
   */
  multiple: PropTypes.bool,

  /**
   * If false, allow dropped items to take over the current browser window
   */
  preventDropOnDocument: PropTypes.bool,

  /**
   * If true, disables click to open the native file selection dialog
   */
  noClick: PropTypes.bool,

  /**
   * If true, disables SPACE/ENTER to open the native file selection dialog.
   * Note that it also stops tracking the focus state.
   */
  noKeyboard: PropTypes.bool,

  /**
   * If true, disables drag 'n' drop
   */
  noDrag: PropTypes.bool,

  /**
   * If true, stops drag event propagation to parents
   */
  noDragEventsBubbling: PropTypes.bool,

  /**
   * Minimum file size (in bytes)
   */
  minSize: PropTypes.number,

  /**
   * Maximum file size (in bytes)
   */
  maxSize: PropTypes.number,

  /**
   * Maximum accepted number of files
   * The default value is 0 which means there is no limitation to how many files are accepted.
   */
  maxFiles: PropTypes.number,

  /**
   * Enable/disable the dropzone
   */
  disabled: PropTypes.bool,

  /**
   * Use this to provide a custom file aggregator
   *
   * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
   */
  getFilesFromEvent: PropTypes.func,

  /**
   * Cb for when closing the file dialog with no selection
   */
  onFileDialogCancel: PropTypes.func,

  /**
   * Cb for when opening the file dialog
   */
  onFileDialogOpen: PropTypes.func,

  /**
   * Set to true to use the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
   * to open the file picker instead of using an `<input type="file">` click event.
   */
  useFsAccessApi: PropTypes.bool,

  /**
   * Set to true to focus the root element on render
   */
  autoFocus: PropTypes.bool,

  /**
   * Cb for when the `dragenter` event occurs.
   *
   * @param {DragEvent} event
   */
  onDragEnter: PropTypes.func,

  /**
   * Cb for when the `dragleave` event occurs
   *
   * @param {DragEvent} event
   */
  onDragLeave: PropTypes.func,

  /**
   * Cb for when the `dragover` event occurs
   *
   * @param {DragEvent} event
   */
  onDragOver: PropTypes.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that this callback is invoked after the `getFilesFromEvent` callback is done.
   *
   * Files are accepted or rejected based on the `accept`, `multiple`, `minSize` and `maxSize` props.
   * `accept` must be a valid [MIME type](http://www.iana.org/assignments/media-types/media-types.xhtml) according to [input element specification](https://www.w3.org/wiki/HTML/Elements/input/file) or a valid file extension.
   * If `multiple` is set to false and additional files are dropped,
   * all files besides the first will be rejected.
   * Any file which does not have a size in the [`minSize`, `maxSize`] range, will be rejected as well.
   *
   * Note that the `onDrop` callback will always be invoked regardless if the dropped files were accepted or rejected.
   * If you'd like to react to a specific scenario, use the `onDropAccepted`/`onDropRejected` props.
   *
   * `onDrop` will provide you with an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects which you can then process and send to a server.
   * For example, with [SuperAgent](https://github.com/visionmedia/superagent) as a http/ajax library:
   *
   * ```js
   * function onDrop(acceptedFiles) {
   *   const req = request.post('/upload')
   *   acceptedFiles.forEach(file => {
   *     req.attach(file.name, file)
   *   })
   *   req.end(callback)
   * }
   * ```
   *
   * @param {File[]} acceptedFiles
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
   */
  onDrop: PropTypes.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are accepted, this callback is not invoked.
   *
   * @param {File[]} files
   * @param {(DragEvent|Event)} event
   */
  onDropAccepted: PropTypes.func,

  /**
   * Cb for when the `drop` event occurs.
   * Note that if no files are rejected, this callback is not invoked.
   *
   * @param {FileRejection[]} fileRejections
   * @param {(DragEvent|Event)} event
   */
  onDropRejected: PropTypes.func,

  /**
   * Cb for when there's some error from any of the promises.
   *
   * @param {Error} error
   */
  onError: PropTypes.func,

  /**
   * Custom validation function. It must return null if there's no errors.
   * @param {File} file
   * @returns {FileError|FileError[]|null}
   */
  validator: PropTypes.func
};
/**
 * A function that is invoked for the `dragenter`,
 * `dragover` and `dragleave` events.
 * It is not invoked if the items are not files (such as link, text, etc.).
 *
 * @callback dragCb
 * @param {DragEvent} event
 */

/**
 * A function that is invoked for the `drop` or input change event.
 * It is not invoked if the items are not files (such as link, text, etc.).
 *
 * @callback dropCb
 * @param {File[]} acceptedFiles List of accepted files
 * @param {FileRejection[]} fileRejections List of rejected files and why they were rejected
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is invoked for the `drop` or input change event.
 * It is not invoked if the items are files (such as link, text, etc.).
 *
 * @callback dropAcceptedCb
 * @param {File[]} files List of accepted files that meet the given criteria
 * (`accept`, `multiple`, `minSize`, `maxSize`)
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is invoked for the `drop` or input change event.
 *
 * @callback dropRejectedCb
 * @param {File[]} files List of rejected files that do not meet the given criteria
 * (`accept`, `multiple`, `minSize`, `maxSize`)
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 */

/**
 * A function that is used aggregate files,
 * in a asynchronous fashion, from drag or input change events.
 *
 * @callback getFilesFromEvent
 * @param {(DragEvent|Event)} event A drag event or input change event (if files were selected via the file dialog)
 * @returns {(File[]|Promise<File[]>)}
 */

/**
 * An object with the current dropzone state.
 *
 * @typedef {object} DropzoneState
 * @property {boolean} isFocused Dropzone area is in focus
 * @property {boolean} isFileDialogActive File dialog is opened
 * @property {boolean} isDragActive Active drag is in progress
 * @property {boolean} isDragAccept Dragged files are accepted
 * @property {boolean} isDragReject Some dragged files are rejected
 * @property {File[]} acceptedFiles Accepted files
 * @property {FileRejection[]} fileRejections Rejected files and why they were rejected
 */

/**
 * An object with the dropzone methods.
 *
 * @typedef {object} DropzoneMethods
 * @property {Function} getRootProps Returns the props you should apply to the root drop container you render
 * @property {Function} getInputProps Returns the props you should apply to hidden file input you render
 * @property {Function} open Open the native file selection dialog
 */

var initialState = {
  isFocused: false,
  isFileDialogActive: false,
  isDragActive: false,
  isDragAccept: false,
  isDragReject: false,
  acceptedFiles: [],
  fileRejections: []
};
/**
 * A React hook that creates a drag 'n' drop area.
 *
 * ```jsx
 * function MyDropzone(props) {
 *   const {getRootProps, getInputProps} = useDropzone({
 *     onDrop: acceptedFiles => {
 *       // do something with the File objects, e.g. upload to some server
 *     }
 *   });
 *   return (
 *     <div {...getRootProps()}>
 *       <input {...getInputProps()} />
 *       <p>Drag and drop some files here, or click to select files</p>
 *     </div>
 *   )
 * }
 * ```
 *
 * @function useDropzone
 *
 * @param {object} props
 * @param {import("./utils").AcceptProp} [props.accept] Set accepted file types.
 * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information.
 * Keep in mind that mime type determination is not reliable across platforms. CSV files,
 * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
 * Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
 * @param {boolean} [props.multiple=true] Allow drag 'n' drop (or selection from the file dialog) of multiple files
 * @param {boolean} [props.preventDropOnDocument=true] If false, allow dropped items to take over the current browser window
 * @param {boolean} [props.noClick=false] If true, disables click to open the native file selection dialog
 * @param {boolean} [props.noKeyboard=false] If true, disables SPACE/ENTER to open the native file selection dialog.
 * Note that it also stops tracking the focus state.
 * @param {boolean} [props.noDrag=false] If true, disables drag 'n' drop
 * @param {boolean} [props.noDragEventsBubbling=false] If true, stops drag event propagation to parents
 * @param {number} [props.minSize=0] Minimum file size (in bytes)
 * @param {number} [props.maxSize=Infinity] Maximum file size (in bytes)
 * @param {boolean} [props.disabled=false] Enable/disable the dropzone
 * @param {getFilesFromEvent} [props.getFilesFromEvent] Use this to provide a custom file aggregator
 * @param {Function} [props.onFileDialogCancel] Cb for when closing the file dialog with no selection
 * @param {boolean} [props.useFsAccessApi] Set to true to use the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API
 * to open the file picker instead of using an `<input type="file">` click event.
 * @param {boolean} autoFocus Set to true to auto focus the root element.
 * @param {Function} [props.onFileDialogOpen] Cb for when opening the file dialog
 * @param {dragCb} [props.onDragEnter] Cb for when the `dragenter` event occurs.
 * @param {dragCb} [props.onDragLeave] Cb for when the `dragleave` event occurs
 * @param {dragCb} [props.onDragOver] Cb for when the `dragover` event occurs
 * @param {dropCb} [props.onDrop] Cb for when the `drop` event occurs.
 * Note that this callback is invoked after the `getFilesFromEvent` callback is done.
 *
 * Files are accepted or rejected based on the `accept`, `multiple`, `minSize` and `maxSize` props.
 * `accept` must be an object with keys as a valid [MIME type](http://www.iana.org/assignments/media-types/media-types.xhtml) according to [input element specification](https://www.w3.org/wiki/HTML/Elements/input/file) and the value an array of file extensions (optional).
 * If `multiple` is set to false and additional files are dropped,
 * all files besides the first will be rejected.
 * Any file which does not have a size in the [`minSize`, `maxSize`] range, will be rejected as well.
 *
 * Note that the `onDrop` callback will always be invoked regardless if the dropped files were accepted or rejected.
 * If you'd like to react to a specific scenario, use the `onDropAccepted`/`onDropRejected` props.
 *
 * `onDrop` will provide you with an array of [File](https://developer.mozilla.org/en-US/docs/Web/API/File) objects which you can then process and send to a server.
 * For example, with [SuperAgent](https://github.com/visionmedia/superagent) as a http/ajax library:
 *
 * ```js
 * function onDrop(acceptedFiles) {
 *   const req = request.post('/upload')
 *   acceptedFiles.forEach(file => {
 *     req.attach(file.name, file)
 *   })
 *   req.end(callback)
 * }
 * ```
 * @param {dropAcceptedCb} [props.onDropAccepted]
 * @param {dropRejectedCb} [props.onDropRejected]
 * @param {(error: Error) => void} [props.onError]
 *
 * @returns {DropzoneState & DropzoneMethods}
 */

function useDropzone() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _defaultProps$props = _objectSpread(_objectSpread({}, defaultProps), props),
      accept = _defaultProps$props.accept,
      disabled = _defaultProps$props.disabled,
      getFilesFromEvent = _defaultProps$props.getFilesFromEvent,
      maxSize = _defaultProps$props.maxSize,
      minSize = _defaultProps$props.minSize,
      multiple = _defaultProps$props.multiple,
      maxFiles = _defaultProps$props.maxFiles,
      onDragEnter = _defaultProps$props.onDragEnter,
      onDragLeave = _defaultProps$props.onDragLeave,
      onDragOver = _defaultProps$props.onDragOver,
      onDrop = _defaultProps$props.onDrop,
      onDropAccepted = _defaultProps$props.onDropAccepted,
      onDropRejected = _defaultProps$props.onDropRejected,
      onFileDialogCancel = _defaultProps$props.onFileDialogCancel,
      onFileDialogOpen = _defaultProps$props.onFileDialogOpen,
      useFsAccessApi = _defaultProps$props.useFsAccessApi,
      autoFocus = _defaultProps$props.autoFocus,
      preventDropOnDocument = _defaultProps$props.preventDropOnDocument,
      noClick = _defaultProps$props.noClick,
      noKeyboard = _defaultProps$props.noKeyboard,
      noDrag = _defaultProps$props.noDrag,
      noDragEventsBubbling = _defaultProps$props.noDragEventsBubbling,
      onError = _defaultProps$props.onError,
      validator = _defaultProps$props.validator;

  var acceptAttr = useMemo(function () {
    return acceptPropAsAcceptAttr(accept);
  }, [accept]);
  var pickerTypes = useMemo(function () {
    return pickerOptionsFromAccept(accept);
  }, [accept]);
  var onFileDialogOpenCb = useMemo(function () {
    return typeof onFileDialogOpen === "function" ? onFileDialogOpen : noop;
  }, [onFileDialogOpen]);
  var onFileDialogCancelCb = useMemo(function () {
    return typeof onFileDialogCancel === "function" ? onFileDialogCancel : noop;
  }, [onFileDialogCancel]);
  /**
   * @constant
   * @type {React.MutableRefObject<HTMLElement>}
   */

  var rootRef = useRef(null);
  var inputRef = useRef(null);

  var _useReducer = useReducer(reducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var isFocused = state.isFocused,
      isFileDialogActive = state.isFileDialogActive;
  var fsAccessApiWorksRef = useRef(typeof window !== "undefined" && window.isSecureContext && useFsAccessApi && canUseFileSystemAccessAPI()); // Update file dialog active state when the window is focused on

  var onWindowFocus = function onWindowFocus() {
    // Execute the timeout only if the file dialog is opened in the browser
    if (!fsAccessApiWorksRef.current && isFileDialogActive) {
      setTimeout(function () {
        if (inputRef.current) {
          var files = inputRef.current.files;

          if (!files.length) {
            dispatch({
              type: "closeDialog"
            });
            onFileDialogCancelCb();
          }
        }
      }, 300);
    }
  };

  useEffect(function () {
    window.addEventListener("focus", onWindowFocus, false);
    return function () {
      window.removeEventListener("focus", onWindowFocus, false);
    };
  }, [inputRef, isFileDialogActive, onFileDialogCancelCb, fsAccessApiWorksRef]);
  var dragTargetsRef = useRef([]);

  var onDocumentDrop = function onDocumentDrop(event) {
    if (rootRef.current && rootRef.current.contains(event.target)) {
      // If we intercepted an event for our instance, let it propagate down to the instance's onDrop handler
      return;
    }

    event.preventDefault();
    dragTargetsRef.current = [];
  };

  useEffect(function () {
    if (preventDropOnDocument) {
      document.addEventListener("dragover", onDocumentDragOver, false);
      document.addEventListener("drop", onDocumentDrop, false);
    }

    return function () {
      if (preventDropOnDocument) {
        document.removeEventListener("dragover", onDocumentDragOver);
        document.removeEventListener("drop", onDocumentDrop);
      }
    };
  }, [rootRef, preventDropOnDocument]); // Auto focus the root when autoFocus is true

  useEffect(function () {
    if (!disabled && autoFocus && rootRef.current) {
      rootRef.current.focus();
    }

    return function () {};
  }, [rootRef, autoFocus, disabled]);
  var onErrCb = useCallback(function (e) {
    if (onError) {
      onError(e);
    } else {
      // Let the user know something's gone wrong if they haven't provided the onError cb.
      console.error(e);
    }
  }, [onError]);
  var onDragEnterCb = useCallback(function (event) {
    event.preventDefault(); // Persist here because we need the event later after getFilesFromEvent() is done

    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [].concat(_toConsumableArray(dragTargetsRef.current), [event.target]);

    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function (files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }

        var fileCount = files.length;
        var isDragAccept = fileCount > 0 && allFilesAccepted({
          files: files,
          accept: acceptAttr,
          minSize: minSize,
          maxSize: maxSize,
          multiple: multiple,
          maxFiles: maxFiles,
          validator: validator
        });
        var isDragReject = fileCount > 0 && !isDragAccept;
        dispatch({
          isDragAccept: isDragAccept,
          isDragReject: isDragReject,
          isDragActive: true,
          type: "setDraggedFiles"
        });

        if (onDragEnter) {
          onDragEnter(event);
        }
      }).catch(function (e) {
        return onErrCb(e);
      });
    }
  }, [getFilesFromEvent, onDragEnter, onErrCb, noDragEventsBubbling, acceptAttr, minSize, maxSize, multiple, maxFiles, validator]);
  var onDragOverCb = useCallback(function (event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event);
    var hasFiles = isEvtWithFiles(event);

    if (hasFiles && event.dataTransfer) {
      try {
        event.dataTransfer.dropEffect = "copy";
      } catch (_unused) {}
      /* eslint-disable-line no-empty */

    }

    if (hasFiles && onDragOver) {
      onDragOver(event);
    }

    return false;
  }, [onDragOver, noDragEventsBubbling]);
  var onDragLeaveCb = useCallback(function (event) {
    event.preventDefault();
    event.persist();
    stopPropagation(event); // Only deactivate once the dropzone and all children have been left

    var targets = dragTargetsRef.current.filter(function (target) {
      return rootRef.current && rootRef.current.contains(target);
    }); // Make sure to remove a target present multiple times only once
    // (Firefox may fire dragenter/dragleave multiple times on the same element)

    var targetIdx = targets.indexOf(event.target);

    if (targetIdx !== -1) {
      targets.splice(targetIdx, 1);
    }

    dragTargetsRef.current = targets;

    if (targets.length > 0) {
      return;
    }

    dispatch({
      type: "setDraggedFiles",
      isDragActive: false,
      isDragAccept: false,
      isDragReject: false
    });

    if (isEvtWithFiles(event) && onDragLeave) {
      onDragLeave(event);
    }
  }, [rootRef, onDragLeave, noDragEventsBubbling]);
  var setFiles = useCallback(function (files, event) {
    var acceptedFiles = [];
    var fileRejections = [];
    files.forEach(function (file) {
      var _fileAccepted = fileAccepted(file, acceptAttr),
          _fileAccepted2 = _slicedToArray(_fileAccepted, 2),
          accepted = _fileAccepted2[0],
          acceptError = _fileAccepted2[1];

      var _fileMatchSize = fileMatchSize(file, minSize, maxSize),
          _fileMatchSize2 = _slicedToArray(_fileMatchSize, 2),
          sizeMatch = _fileMatchSize2[0],
          sizeError = _fileMatchSize2[1];

      var customErrors = validator ? validator(file) : null;

      if (accepted && sizeMatch && !customErrors) {
        acceptedFiles.push(file);
      } else {
        var errors = [acceptError, sizeError];

        if (customErrors) {
          errors = errors.concat(customErrors);
        }

        fileRejections.push({
          file: file,
          errors: errors.filter(function (e) {
            return e;
          })
        });
      }
    });

    if (!multiple && acceptedFiles.length > 1 || multiple && maxFiles >= 1 && acceptedFiles.length > maxFiles) {
      // Reject everything and empty accepted files
      acceptedFiles.forEach(function (file) {
        fileRejections.push({
          file: file,
          errors: [TOO_MANY_FILES_REJECTION]
        });
      });
      acceptedFiles.splice(0);
    }

    dispatch({
      acceptedFiles: acceptedFiles,
      fileRejections: fileRejections,
      type: "setFiles"
    });

    if (onDrop) {
      onDrop(acceptedFiles, fileRejections, event);
    }

    if (fileRejections.length > 0 && onDropRejected) {
      onDropRejected(fileRejections, event);
    }

    if (acceptedFiles.length > 0 && onDropAccepted) {
      onDropAccepted(acceptedFiles, event);
    }
  }, [dispatch, multiple, acceptAttr, minSize, maxSize, maxFiles, onDrop, onDropAccepted, onDropRejected, validator]);
  var onDropCb = useCallback(function (event) {
    event.preventDefault(); // Persist here because we need the event later after getFilesFromEvent() is done

    event.persist();
    stopPropagation(event);
    dragTargetsRef.current = [];

    if (isEvtWithFiles(event)) {
      Promise.resolve(getFilesFromEvent(event)).then(function (files) {
        if (isPropagationStopped(event) && !noDragEventsBubbling) {
          return;
        }

        setFiles(files, event);
      }).catch(function (e) {
        return onErrCb(e);
      });
    }

    dispatch({
      type: "reset"
    });
  }, [getFilesFromEvent, setFiles, onErrCb, noDragEventsBubbling]); // Fn for opening the file dialog programmatically

  var openFileDialog = useCallback(function () {
    // No point to use FS access APIs if context is not secure
    // https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts#feature_detection
    if (fsAccessApiWorksRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb(); // https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker

      var opts = {
        multiple: multiple,
        types: pickerTypes
      };
      window.showOpenFilePicker(opts).then(function (handles) {
        return getFilesFromEvent(handles);
      }).then(function (files) {
        setFiles(files, null);
        dispatch({
          type: "closeDialog"
        });
      }).catch(function (e) {
        // AbortError means the user canceled
        if (isAbort(e)) {
          onFileDialogCancelCb(e);
          dispatch({
            type: "closeDialog"
          });
        } else if (isSecurityError(e)) {
          fsAccessApiWorksRef.current = false; // CORS, so cannot use this API
          // Try using the input

          if (inputRef.current) {
            inputRef.current.value = null;
            inputRef.current.click();
          } else {
            onErrCb(new Error("Cannot open the file picker because the https://developer.mozilla.org/en-US/docs/Web/API/File_System_Access_API is not supported and no <input> was provided."));
          }
        } else {
          onErrCb(e);
        }
      });
      return;
    }

    if (inputRef.current) {
      dispatch({
        type: "openDialog"
      });
      onFileDialogOpenCb();
      inputRef.current.value = null;
      inputRef.current.click();
    }
  }, [dispatch, onFileDialogOpenCb, onFileDialogCancelCb, useFsAccessApi, setFiles, onErrCb, pickerTypes, multiple]); // Cb to open the file dialog when SPACE/ENTER occurs on the dropzone

  var onKeyDownCb = useCallback(function (event) {
    // Ignore keyboard events bubbling up the DOM tree
    if (!rootRef.current || !rootRef.current.isEqualNode(event.target)) {
      return;
    }

    if (event.key === " " || event.key === "Enter" || event.keyCode === 32 || event.keyCode === 13) {
      event.preventDefault();
      openFileDialog();
    }
  }, [rootRef, openFileDialog]); // Update focus state for the dropzone

  var onFocusCb = useCallback(function () {
    dispatch({
      type: "focus"
    });
  }, []);
  var onBlurCb = useCallback(function () {
    dispatch({
      type: "blur"
    });
  }, []); // Cb to open the file dialog when click occurs on the dropzone

  var onClickCb = useCallback(function () {
    if (noClick) {
      return;
    } // In IE11/Edge the file-browser dialog is blocking, therefore, use setTimeout()
    // to ensure React can handle state changes
    // See: https://github.com/react-dropzone/react-dropzone/issues/450


    if (isIeOrEdge()) {
      setTimeout(openFileDialog, 0);
    } else {
      openFileDialog();
    }
  }, [noClick, openFileDialog]);

  var composeHandler = function composeHandler(fn) {
    return disabled ? null : fn;
  };

  var composeKeyboardHandler = function composeKeyboardHandler(fn) {
    return noKeyboard ? null : composeHandler(fn);
  };

  var composeDragHandler = function composeDragHandler(fn) {
    return noDrag ? null : composeHandler(fn);
  };

  var stopPropagation = function stopPropagation(event) {
    if (noDragEventsBubbling) {
      event.stopPropagation();
    }
  };

  var getRootProps = useMemo(function () {
    return function () {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref2$refKey = _ref2.refKey,
          refKey = _ref2$refKey === void 0 ? "ref" : _ref2$refKey,
          role = _ref2.role,
          onKeyDown = _ref2.onKeyDown,
          onFocus = _ref2.onFocus,
          onBlur = _ref2.onBlur,
          onClick = _ref2.onClick,
          onDragEnter = _ref2.onDragEnter,
          onDragOver = _ref2.onDragOver,
          onDragLeave = _ref2.onDragLeave,
          onDrop = _ref2.onDrop,
          rest = _objectWithoutProperties(_ref2, _excluded3);

      return _objectSpread(_objectSpread(_defineProperty({
        onKeyDown: composeKeyboardHandler(composeEventHandlers(onKeyDown, onKeyDownCb)),
        onFocus: composeKeyboardHandler(composeEventHandlers(onFocus, onFocusCb)),
        onBlur: composeKeyboardHandler(composeEventHandlers(onBlur, onBlurCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onClickCb)),
        onDragEnter: composeDragHandler(composeEventHandlers(onDragEnter, onDragEnterCb)),
        onDragOver: composeDragHandler(composeEventHandlers(onDragOver, onDragOverCb)),
        onDragLeave: composeDragHandler(composeEventHandlers(onDragLeave, onDragLeaveCb)),
        onDrop: composeDragHandler(composeEventHandlers(onDrop, onDropCb)),
        role: typeof role === "string" && role !== "" ? role : "presentation"
      }, refKey, rootRef), !disabled && !noKeyboard ? {
        tabIndex: 0
      } : {}), rest);
    };
  }, [rootRef, onKeyDownCb, onFocusCb, onBlurCb, onClickCb, onDragEnterCb, onDragOverCb, onDragLeaveCb, onDropCb, noKeyboard, noDrag, disabled]);
  var onInputElementClick = useCallback(function (event) {
    event.stopPropagation();
  }, []);
  var getInputProps = useMemo(function () {
    return function () {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref3$refKey = _ref3.refKey,
          refKey = _ref3$refKey === void 0 ? "ref" : _ref3$refKey,
          onChange = _ref3.onChange,
          onClick = _ref3.onClick,
          rest = _objectWithoutProperties(_ref3, _excluded4);

      var inputProps = _defineProperty({
        accept: acceptAttr,
        multiple: multiple,
        type: "file",
        style: {
          display: "none"
        },
        onChange: composeHandler(composeEventHandlers(onChange, onDropCb)),
        onClick: composeHandler(composeEventHandlers(onClick, onInputElementClick)),
        tabIndex: -1
      }, refKey, inputRef);

      return _objectSpread(_objectSpread({}, inputProps), rest);
    };
  }, [inputRef, accept, multiple, onDropCb, disabled]);
  return _objectSpread(_objectSpread({}, state), {}, {
    isFocused: isFocused && !disabled,
    getRootProps: getRootProps,
    getInputProps: getInputProps,
    rootRef: rootRef,
    inputRef: inputRef,
    open: composeHandler(openFileDialog)
  });
}
/**
 * @param {DropzoneState} state
 * @param {{type: string} & DropzoneState} action
 * @returns {DropzoneState}
 */

function reducer(state, action) {
  /* istanbul ignore next */
  switch (action.type) {
    case "focus":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: true
      });

    case "blur":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFocused: false
      });

    case "openDialog":
      return _objectSpread(_objectSpread({}, initialState), {}, {
        isFileDialogActive: true
      });

    case "closeDialog":
      return _objectSpread(_objectSpread({}, state), {}, {
        isFileDialogActive: false
      });

    case "setDraggedFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        isDragActive: action.isDragActive,
        isDragAccept: action.isDragAccept,
        isDragReject: action.isDragReject
      });

    case "setFiles":
      return _objectSpread(_objectSpread({}, state), {}, {
        acceptedFiles: action.acceptedFiles,
        fileRejections: action.fileRejections
      });

    case "reset":
      return _objectSpread({}, initialState);

    default:
      return state;
  }
}

function noop() {}

const useStyles = makeG2STyles()((theme, { height }) => ({
    root: {
        position: 'relative',
        width: '100%',
        height: height
    },
    dropZone: {
        width: '100%',
        height: height,
        background: 'rgb(237, 237, 237)',
        border: 'dashed rgba(209,202,203,1) 2px',
        borderRadius: theme.borderRadius,
        position: 'relative',
        cursor: 'pointer',
        '&:focus': {
            outline: 'none'
        }
    },
    error: {
        color: '#bd1313',
        fontSize: '12px'
    },
    image: {
        width: '100%',
        borderRadius: theme.borderRadius,
        objectFit: 'contain',
        height: height,
        position: 'relative'
    },
    clear: {
        color: '#757575',
        width: '40px',
        height: '40px',
        display: 'none',
        position: 'absolute',
        transform: 'translate(-50%,-50%)',
        top: '50%',
        left: '50%'
    },
    container: {
        width: '100%',
        height: height,
        position: 'relative',
        cursor: 'pointer',
        '&:hover img': {
            opacity: '0.4'
        },
        '&:hover svg': {
            display: 'block'
        }
    },
    add: {
        color: 'rgba(209,202,203,1)',
        width: '70px',
        height: '70px',
        position: 'absolute',
        transform: 'translate(-50%,-50%)',
        top: '50%',
        left: '50%'
    },
    hidder: {
        position: 'fixed',
        top: 0,
        left: 0
    }
}));
const DropPictureBase = (props, ref) => {
    const { onPictureDropped, onRemovePicture, className, style, readOnly = false, pictureUrl, defaultPicture = '', maxSize = 10 * 1024 * 1024, customErrorMessage, id, alt = 'A picture', classes, styles, isLoading = false, height = '100%' } = props, other = __rest(props, ["onPictureDropped", "onRemovePicture", "className", "style", "readOnly", "pictureUrl", "defaultPicture", "maxSize", "customErrorMessage", "id", "alt", "classes", "styles", "isLoading", "height"]);
    const [error, setError] = useState(customErrorMessage);
    const [unFound, setUnFound] = useState(false);
    const defaultStyles = useStyles({ height });
    const { t } = useTranslation();
    useEffect(() => {
        setError(customErrorMessage);
    }, [customErrorMessage]);
    useEffect(() => {
        setUnFound(false);
    }, [readOnly]);
    const onUpload = useCallback((acceptedFiles) => {
        onPictureDropped && onPictureDropped(acceptedFiles[0]);
    }, [onPictureDropped]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && onRemovePicture) {
            onRemovePicture();
        }
    }, [onRemovePicture]);
    const onReject = useCallback((fileRejections) => {
        const code = fileRejections[0].errors[0].code;
        setError(t('g2.' + code, {
            formats: ['png', 'jpeg'].join(` ${t('g2.or')} `),
            sizeLimit: maxSize / 1024 / 1024
        }));
    }, [maxSize]);
    const { getRootProps, getInputProps } = useDropzone(Object.assign(Object.assign({}, other), { multiple: false, accept: {
            'image/*': ['.jpeg', '.png']
        }, maxSize: maxSize, onDropAccepted: onUpload, onDropRejected: onReject }));
    const onError = useCallback(() => setUnFound(true), []);
    if (isLoading)
        return (React__default.createElement(Skeleton, { className: defaultStyles.cx('AruiDropzone-skeleton'), sx: {
                width: '100%',
                height: height,
                transform: 'none'
            }, animation: 'wave' }));
    if (readOnly)
        return (React__default.createElement("img", { src: !unFound ? pictureUrl : defaultPicture, className: defaultStyles.cx(defaultStyles.classes.image, classes === null || classes === void 0 ? void 0 : classes.image, 'AruiDropzone-image', className), style: styles === null || styles === void 0 ? void 0 : styles.image, alt: alt, onError: onError }));
    if (!pictureUrl)
        return (React__default.createElement("div", { ref: ref, className: defaultStyles.cx(defaultStyles.classes.root, 'AruiDropzone-root', className), style: style },
            React__default.createElement(Tooltip, { helperText: t('g2.addPicture'), className: classes === null || classes === void 0 ? void 0 : classes.tooltip, style: styles === null || styles === void 0 ? void 0 : styles.tooltip },
                React__default.createElement(Paper, Object.assign({ elevation: 0, className: defaultStyles.cx(defaultStyles.classes.dropZone, 'AruiDropzone-dropzone', classes === null || classes === void 0 ? void 0 : classes.dropZone), style: styles === null || styles === void 0 ? void 0 : styles.dropZone }, getRootProps()),
                    React__default.createElement("input", Object.assign({ id: id }, getInputProps())),
                    React__default.createElement(AddPhotoAlternate, { className: defaultStyles.cx(defaultStyles.classes.add, classes === null || classes === void 0 ? void 0 : classes.addPictureIcon), style: styles === null || styles === void 0 ? void 0 : styles.addPictureIcon }))),
            !!error && (React__default.createElement(Typography, { className: defaultStyles.cx(defaultStyles.classes.error, 'AruiDropzone-error', classes === null || classes === void 0 ? void 0 : classes.errorMessage), style: styles === null || styles === void 0 ? void 0 : styles.errorMessage, variant: 'body2', align: 'center' }, error))));
    return (React__default.createElement(Tooltip, { helperText: t('g2.removePicture'), className: classes === null || classes === void 0 ? void 0 : classes.tooltip, style: styles === null || styles === void 0 ? void 0 : styles.tooltip },
        React__default.createElement("div", { ref: ref, className: defaultStyles.cx(defaultStyles.classes.container, 'AruiDropzone-root', className), style: style, id: id, onClick: onRemovePicture, onKeyDown: handleKeyDown },
            React__default.createElement("img", { src: pictureUrl, className: defaultStyles.cx(defaultStyles.classes.image, 'AruiDropzone-image', classes === null || classes === void 0 ? void 0 : classes.image), style: styles === null || styles === void 0 ? void 0 : styles.image, alt: alt, onError: onError }),
            React__default.createElement(Clear, { className: defaultStyles.cx(defaultStyles.classes.clear, 'AruiDropzone-clearIcon', classes === null || classes === void 0 ? void 0 : classes.clearIcon), style: styles === null || styles === void 0 ? void 0 : styles.clearIcon }))));
};
const DropPicture = forwardRef(DropPictureBase);

const ToggleButtons = styled((props) => {
    const { onChangeExclusive, onChangeNonExclusive, options, exclusive, className, value, values = [], classes, styles } = props, other = __rest(props, ["onChangeExclusive", "onChangeNonExclusive", "options", "exclusive", "className", "value", "values", "classes", "styles"]);
    const onChangeMemoized = useCallback((event, value) => {
        if (exclusive) {
            onChangeExclusive && onChangeExclusive(event, value);
        }
        else {
            onChangeNonExclusive && onChangeNonExclusive(event, value);
        }
    }, [exclusive, onChangeExclusive, onChangeNonExclusive]);
    const buttons = useMemo(() => options.map((option) => (React__default.createElement(ToggleButton, { className: cx('AruiToggleButtons-button', classes === null || classes === void 0 ? void 0 : classes.button), style: styles === null || styles === void 0 ? void 0 : styles.button, key: option.key, value: option.key }, option.content))), [options, classes === null || classes === void 0 ? void 0 : classes.button, styles === null || styles === void 0 ? void 0 : styles.button]);
    return (React__default.createElement(ToggleButtonGroup, Object.assign({ color: 'primary', className: cx('AruiToggleButtons-root', className), onChange: onChangeMemoized, value: exclusive ? value : values, exclusive: exclusive }, other), buttons));
})(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        border: `2px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        textTransform: 'none'
    },
    '& .MuiToggleButtonGroup-grouped:hover': {
        background: `${theme.palette.primary.main}4D`
    },
    '& .MuiToggleButtonGroup-grouped.Mui-selected:hover': {
        background: `${theme.palette.primary.main}B3`
    },
    '& .MuiToggleButtonGroup-grouped.Mui-selected': {
        color: 'white',
        background: theme.palette.primary.main
    }
}));

const UserAvatar = (props) => {
    const { name = '', size = 'small', sx } = props, other = __rest(props, ["name", "size", "sx"]);
    const nameTrimmed = useMemo(() => name.trim(), [name]);
    const attr = useMemo(() => nameTrimmed
        ? stringToAvatarAttributs(name)
        : { color: '#6768791A', label: '' }, [name]);
    const sizeNumber = size === 'small' ? '40px' : size === 'medium' ? '60px' : '100px';
    return (React__default.createElement(Avatar, Object.assign({ sx: Object.assign({ bgcolor: attr.color, width: sizeNumber, height: sizeNumber }, sx) }, other),
        React__default.createElement(Typography, { variant: size === 'small' ? 'body1' : size === 'medium' ? 'h6' : 'h4' }, attr.label)));
};

const Presentation = (props) => {
    const { label, subLabel, imgSrc, sx, description, displayAvatar = true } = props, other = __rest(props, ["label", "subLabel", "imgSrc", "sx", "description", "displayAvatar"]);
    return (React__default.createElement(Stack, Object.assign({ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', direction: 'row', sx: Object.assign({ gap: '8px' }, sx) }, other),
        imgSrc ? (React__default.createElement(Box, { sx: {
                display: 'flex',
                '& .presentationImage': {
                    width: '50px',
                    maxHeight: '70px',
                    borderRadius: (theme) => `${Number(theme.shape.borderRadius) / 2}px`
                }
            } },
            React__default.createElement("img", { src: imgSrc, alt: description, className: 'presentationImage' }))) : (displayAvatar && React__default.createElement(UserAvatar, { name: label, "aria-label": description })),
        React__default.createElement(Stack, null,
            React__default.createElement(Typography, { align: 'left', variant: 'subtitle2' }, label),
            subLabel && (React__default.createElement(Typography, { sx: { color: '#9E9E9E' }, align: 'left', variant: 'caption' }, subLabel)))));
};

const Chip = forwardRef((props, ref) => {
    const { sx, color = '#676879', bold = true, className } = props, other = __rest(props, ["sx", "color", "bold", "className"]);
    return (React__default.createElement(Chip$1, Object.assign({ ref: ref, className: cx('AruiChip-root', className), sx: Object.assign({ bgcolor: color + '1A', color: color, borderRadius: (theme) => `${theme.shape.borderRadius}px`, height: 'unset', padding: '4px 0', '& .MuiChip-deleteIcon': {
                color: color,
                fontSize: '19px'
            }, '& .MuiChip-label': {
                fontWeight: bold ? 600 : '',
                padding: '0 10px'
            } }, sx), deleteIcon: React__default.createElement(ClearRounded, { sx: { fontSize: '19px' } }) }, other)));
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".m_d46a4834 {\n  position: relative;\n  border: calc(0.0625rem * var(--mantine-scale)) dashed;\n  color: var(--mantine-color-text);\n  padding: var(--mantine-spacing-md);\n  border-radius: var(--dropzone-radius);\n  cursor: pointer;\n  user-select: none;\n  transition:\n    background-color 100ms ease,\n    border-color 100ms ease;\n}\n\n  .m_d46a4834:where([data-loading]),\n  .m_d46a4834:where(:not([data-activate-on-click])) {\n    cursor: default;\n  }\n\n  :where([data-mantine-color-scheme='light']) .m_d46a4834 {\n    background-color: var(--mantine-color-white);\n    border-color: var(--mantine-color-gray-4);\n}\n\n  :where([data-mantine-color-scheme='dark']) .m_d46a4834 {\n    background-color: var(--mantine-color-dark-6);\n    border-color: var(--mantine-color-dark-4);\n}\n\n  @media (hover: hover) {\n      :where([data-mantine-color-scheme='light']) .m_d46a4834:hover:where([data-activate-on-click]:not([data-loading])) {\n        background-color: var(--mantine-color-gray-0);\n  }\n\n      :where([data-mantine-color-scheme='dark']) .m_d46a4834:hover:where([data-activate-on-click]:not([data-loading])) {\n        background-color: var(--mantine-color-dark-5);\n  }\n}\n\n  @media (hover: none) {\n      :where([data-mantine-color-scheme='light']) .m_d46a4834:active:where([data-activate-on-click]:not([data-loading])) {\n        background-color: var(--mantine-color-gray-0);\n  }\n\n      :where([data-mantine-color-scheme='dark']) .m_d46a4834:active:where([data-activate-on-click]:not([data-loading])) {\n        background-color: var(--mantine-color-dark-5);\n  }\n}\n\n  .m_d46a4834:where([data-accept]) {\n    background-color: var(--dropzone-accept-bg);\n    border-color: var(--dropzone-accept-bg);\n    color: var(--dropzone-accept-color);\n  }\n\n  .m_d46a4834:where([data-reject]) {\n    background-color: var(--dropzone-reject-bg);\n    border-color: var(--dropzone-reject-bg);\n    color: var(--dropzone-reject-color);\n  }\n\n.m_b85f7144 {\n  pointer-events: none;\n  user-select: none;\n}\n\n.m_b85f7144:where([data-enable-pointer-events]) {\n    pointer-events: all;\n  }\n\n.m_96f6e9ad {\n  position: fixed;\n  inset: 0;\n  background-color: var(--mantine-color-body);\n  display: flex;\n  flex-direction: column;\n  padding: var(--mantine-spacing-xs);\n  transition: opacity 100ms ease;\n}\n\n.m_96f6e9ad .m_7946116d {\n    flex: 1;\n  }\n";
styleInject(css_248z);

const DocumentHandler = (props) => {
    const { label, getFileUrl, isRequired = false, fileTypesAllowed, customErrorMessage, multiple = false, onAdd, onDelete, onEditFileName, onDownload, onView, isLoading = false, className, dropzoneProps, style, uploaded, outterLabel, id } = props, otherProps = __rest(props, ["label", "getFileUrl", "isRequired", "fileTypesAllowed", "customErrorMessage", "multiple", "onAdd", "onDelete", "onEditFileName", "onDownload", "onView", "isLoading", "className", "dropzoneProps", "style", "uploaded", "outterLabel", "id"]);
    const [error, setError] = useState(customErrorMessage);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const dropzoneRef = useRef(null);
    const { t } = useTranslation();
    const downHandler = useCallback((event) => {
        if (event.key === 'Enter') {
            event.currentTarget.click();
        }
    }, []);
    useEffect(() => {
        if (dropzoneRef.current) {
            //@ts-ignore
            dropzoneRef.current.onkeydown = downHandler;
        }
    }, [dropzoneRef.current]);
    useEffect(() => {
        setError(customErrorMessage);
    }, [customErrorMessage]);
    const onViewMemoized = useCallback(async () => {
        if (onView) {
            onView();
        }
        else if (getFileUrl) {
            setLoading(true);
            const url = await getFileUrl();
            if (url)
                window.open(url, '_blank');
            setLoading(false);
        }
    }, [onView, getFileUrl, label]);
    const onDownloadMemoized = useCallback(async () => {
        if (onDownload) {
            onDownload();
        }
        else if (getFileUrl && label) {
            setLoading(true);
            const url = await getFileUrl();
            if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.download = label;
                link.click();
            }
            setLoading(false);
        }
    }, [onDownload, getFileUrl, label]);
    const onRejectMemoized = useCallback((fileRejections) => {
        const code = fileRejections[0].errors[0].code;
        setError(t('g2.' + code, {
            formats: ['png', 'jpeg'].join(', '),
            sizeLimit: (dropzoneProps === null || dropzoneProps === void 0 ? void 0 : dropzoneProps.maxSize)
                ? (dropzoneProps === null || dropzoneProps === void 0 ? void 0 : dropzoneProps.maxSize) / 1024 / 1024
                : 50
        }));
    }, [t, dropzoneProps === null || dropzoneProps === void 0 ? void 0 : dropzoneProps.maxSize]);
    const onDrop = useCallback((files) => {
        onAdd && onAdd(files);
        setError(undefined);
    }, [onAdd]);
    const accept = useMemo(() => {
        if (fileTypesAllowed) {
            return fileTypesAllowed.map((type) => MIME_TYPES[type]);
        }
        return;
    }, [fileTypesAllowed]);
    const childrenProps = {
        uploaded,
        error,
        label,
        isRequired,
        onDelete,
        onEditFileName,
        onDownload: onDownloadMemoized,
        onView: onViewMemoized,
        fileTypesAllowed,
        isLoading: isLoading || loading
    };
    if (uploaded) {
        return (React__default.createElement(React__default.Fragment, null,
            outterLabel && React__default.createElement(InputLabel, { htmlFor: id }, outterLabel),
            React__default.createElement(Stack, Object.assign({}, otherProps, { id: id, className: cx('AruiDocumentHandler-root', className), style: style, sx: Object.assign({ width: '100%', minWidth: '100px', overflow: 'hidden', borderRadius: theme.borderRadius + 'px', background: '#F5F5F5', cursor: 'pointer' }, otherProps === null || otherProps === void 0 ? void 0 : otherProps.sx), onClick: onViewMemoized }),
                React__default.createElement(DropzoneChildren, Object.assign({}, childrenProps)))));
    }
    return (React__default.createElement(Box, { sx: {
            width: '100%',
            minWidth: '100px',
            '& .AruiDocumentHandler-root': {
                width: '100%',
                minWidth: '100px',
                overflow: 'hidden',
                borderRadius: theme.borderRadius + 'px',
                border: '2px dashed rgb(189, 189, 189)',
                borderColor: error ? theme.colors.error : '#BDBDBD',
                padding: '0px',
                pointerEvents: isLoading || loading ? 'none' : 'auto',
                opacity: isLoading || loading ? 0.8 : 1,
                backgroundColor: 'rgb(255, 255, 255)'
            },
            '&:hover .AruiDocumentHandler-root': {
                backgroundColor: 'rgb(248, 249, 250)'
            }
        } },
        outterLabel && React__default.createElement(InputLabel, { htmlFor: id }, outterLabel),
        React__default.createElement(Dropzone$1, Object.assign({ className: cx('AruiDocumentHandler-root', className), onDrop: onDrop, onReject: onRejectMemoized, accept: accept, ref: dropzoneRef, maxSize: 50 * 1024 * 1024, multiple: multiple && !isRequired, disabled: isLoading || loading }, dropzoneProps),
            React__default.createElement(DropzoneChildren, Object.assign({}, childrenProps)))));
};
const DropzoneChildren = (props) => {
    const { uploaded, error, isRequired, label, onDelete, onDownload, onEditFileName, onView, fileTypesAllowed, isLoading } = props;
    const { t } = useTranslation();
    const generatedLabel = useMemo(() => {
        if (error)
            return (React__default.createElement(Typography, { sx: { color: (theme) => theme.palette.error.main }, variant: 'body2' }, error));
        const labels = [];
        if (isRequired && !uploaded) {
            labels.push(React__default.createElement(Typography, { key: 'isRequired', sx: { color: (theme) => theme.palette.warning.main, fontWeight: 700 }, variant: 'subtitle2' }, t('g2.required')));
        }
        if (fileTypesAllowed && !uploaded) {
            labels.push(React__default.createElement(Typography, { key: 'fileType', sx: { color: '#676879', textTransform: 'uppercase' }, variant: 'subtitle2' }, fileTypesAllowed.join(', ')));
        }
        if (label) {
            const labelParts = label.split('.');
            const labelName = labelParts[0];
            const labelType = labelParts[1];
            if (labelType) {
                labels.push(React__default.createElement(Typography, { key: 'fileType-lastPart', sx: { color: '#676879', textTransform: 'uppercase' }, variant: 'subtitle2' }, labelType));
            }
            labels.push(React__default.createElement(Typography, { key: 'label', sx: {
                    color: '#676879',
                    lineBreak: 'anywhere',
                    WebkitLineClamp: 2,
                    lineClamp: '2',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                }, variant: 'body2' }, labelName));
        }
        return labels;
    }, [error, label, isRequired, uploaded, fileTypesAllowed]);
    const generatedIcon = useMemo(() => {
        if (isLoading) {
            return (React__default.createElement(CircularProgress, { size: 24, sx: {
                    color: '#676879'
                } }));
        }
        if (uploaded) {
            return React__default.createElement(CloudDoneRounded, { color: 'success' });
        }
        if (error) {
            return React__default.createElement(ErrorRounded, { color: 'error' });
        }
        if (isRequired) {
            return React__default.createElement(CloudUploadRounded, { color: 'warning' });
        }
        return React__default.createElement(CloudUploadRounded, { sx: { color: '#676879' } });
    }, [isRequired, error, isLoading]);
    const generatedActions = useMemo(() => {
        const iconSx = {
            width: '20px',
            height: '20px',
            color: '#676879',
            cursor: 'pointer',
            opacity: 0.6,
            '&:hover': {
                opacity: 1
            }
        };
        if (!uploaded) {
            return React__default.createElement(UploadRounded, { sx: iconSx });
        }
        else {
            const actions = [];
            if (onDelete) {
                actions.push(React__default.createElement(DeleteRounded, { key: 'onDelete', sx: iconSx, onClick: (e) => {
                        e.stopPropagation();
                        onDelete();
                    } }));
            }
            // if (onEditFileName) {
            //     actions.push(
            //         <IconButton sx={buttonSx} onClick={}>
            //             <EditRounded sx={iconSx} />
            //         </IconButton>
            //     )
            // }
            if (onDownload) {
                actions.push(React__default.createElement(DownloadRounded, { sx: iconSx, key: 'onDownload', onClick: (e) => {
                        e.stopPropagation();
                        onDownload();
                    } }));
            }
            if (onView) {
                actions.push(React__default.createElement(VisibilityRounded, { sx: iconSx, key: 'onView', onClick: (e) => {
                        e.stopPropagation();
                        onView();
                    } }));
            }
            return actions;
        }
    }, [uploaded, onDelete, onEditFileName, onDownload, onView]);
    return (React__default.createElement(Stack, { direction: 'row', sx: {
            alignItems: 'center',
            padding: '10px 14px',
            gap: (theme) => theme.spacing(2)
        } },
        generatedIcon,
        React__default.createElement(Divider, { sx: { borderColor: '#BDBDBD' }, orientation: 'vertical', flexItem: true }),
        React__default.createElement(Stack, { direction: 'row', sx: {
                alignItems: 'center',
                flex: 1,
                gap: (theme) => theme.spacing(1)
            }, divider: React__default.createElement(Divider, { sx: { width: '8px', borderColor: '#BDBDBD' } }) }, generatedLabel),
        React__default.createElement(Stack, { direction: 'row', sx: {
                alignItems: 'center',
                gap: (theme) => theme.spacing(1.2)
            } }, generatedActions)));
};

const useLocalDocumentHandler = () => {
    const [file, setFile] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const onAdd = useCallback(async (files) => {
        setIsLoading(true);
        const file = files[0];
        setFile(file);
        setIsLoading(false);
    }, []);
    const onDelete = useCallback(() => {
        setFile(undefined);
    }, []);
    const getFileUrl = useCallback(() => (file ? URL.createObjectURL(file) : ''), [file]);
    const resetFile = useCallback(() => setFile(undefined), []);
    return {
        file,
        getFileUrl,
        resetFile,
        documentHandlerProps: {
            uploaded: !!file,
            getFileUrl,
            isLoading,
            onAdd,
            onDelete,
            dropzoneProps: {
                multiple: false
            }
        }
    };
};

const useLocalStorageDocumentHandler = (fileKey) => {
    const [fileUrl, setFileUrl] = useLocalStorage({
        key: fileKey
    });
    const [fileName, setFileName] = useLocalStorage({
        key: fileKey + '-fileName'
    });
    const [isLoading, setIsLoading] = useState(false);
    const getFile = useCallback(() => (fileUrl ? Base64ToFile(fileUrl, fileName) : undefined), [fileUrl, fileName]);
    const getFileUrl = useCallback(() => fileUrl
        ? URL.createObjectURL(Base64ToFile(fileUrl, fileName))
        : undefined, [fileUrl]);
    const onAdd = useCallback(async (files) => {
        setIsLoading(true);
        const file = files[0];
        setFileName(file.name);
        const url = await fileToBase64(file);
        setFileUrl(url);
        setIsLoading(false);
    }, []);
    const onDelete = useCallback(() => {
        setFileUrl('');
        setFileName('');
    }, []);
    return {
        getFile,
        fileUrl,
        fileName,
        documentHandlerProps: {
            uploaded: !!fileUrl,
            getFileUrl,
            isLoading,
            onAdd,
            onDelete,
            dropzoneProps: {
                multiple: false,
                maxSize: 4 * 1024 * 1024
            }
        }
    };
};

const Breadcrumbs = (props) => {
    const { crumbs, sx, className, classes, styles } = props, other = __rest(props, ["crumbs", "sx", "className", "classes", "styles"]);
    const crumbsDisplay = useMemo(() => crumbs === null || crumbs === void 0 ? void 0 : crumbs.map((crumb, index) => index !== crumbs.length - 1 ? (React__default.createElement(Link, { component: Link$1, componentProps: {
            to: crumb.url
        }, key: crumb.url, sx: {
            color: '#757575',
            textDecoration: 'none !important',
            '&:hover': {
                textDecoration: 'underline !important'
            }
        }, className: cx('AruiBreadcrumbs-link', classes === null || classes === void 0 ? void 0 : classes.link), style: styles === null || styles === void 0 ? void 0 : styles.link }, crumb.label)) : (React__default.createElement(Typography, { sx: {
            color: '#424242'
        }, key: crumb.url, className: cx('AruiBreadcrumbs-currentPage', classes === null || classes === void 0 ? void 0 : classes.currentPage), style: styles === null || styles === void 0 ? void 0 : styles.currentPage }, crumb.label))), [crumbs, classes, styles]);
    return (React__default.createElement(Breadcrumbs$1, Object.assign({ separator: React__default.createElement(NavigateNextRounded, { sx: {
                color: '#757575'
            } }) }, other, { className: cx('AruiBreadcrumbs-root', className), sx: Object.assign({ '& .MuiBreadcrumbs-separator': {
                margin: '0 4px'
            } }, sx), "aria-label": 'breadcrumb' }), crumbsDisplay));
};

export { Actions, ActionsWrapper, BackButton, Breadcrumbs, Button, Chip, ContextMenu, CopyToClipboard, DeleteButton, DocumentHandler, DropPicture, DropzoneChildren, EditButton, FixedPagination, LabelSwitch, Link, LinkButton, Loading, Menu, MoreOptions, Pagination, Presentation, Ticket, Timeline, ToggleButtons, UserAvatar, useActionFeedback, useLocalDocumentHandler, useLocalStorageDocumentHandler };
//# sourceMappingURL=index.es.js.map
